import qs from 'qs';

const axios = require('axios');
const validate = require('jsonschema').validate;

/** Base class handling common functions of the api */
class SmartmieteAPI {

	/**
	 * Creates a new instance of SmartmieteAPI
	 *
	 * @return {SmartmieteAPI} The new SmartmieteAPI object
	 */
	constructor() {

		if ('NODE_ENV' in process.env && process.env.NODE_ENV === 'development') {
			if (!('VUE_APP_API_BASE_URL' in process.env) || !('VUE_APP_AUTH_URL' in process.env)) {
				this.logger('error', 'Your are running this app as developer, but are still talking to the production servers. (Hint: Add your own .env.* file in the project, or just ask Kay');
			}
		}

		let apiKeyKeyPrefix = ('VUE_APP_API_KEY_KEY_PREFIX' in process.env) ? process.env.VUE_APP_API_KEY_KEY_PREFIX : '___api_key_husleie_api_v2_';
		/** @private */ this.baseUrl = ('VUE_APP_API_BASE_URL' in process.env) ? process.env.VUE_APP_API_BASE_URL : 'https://api.husleie.no/api/v2';
		/** @private */ this.authUrl = ('VUE_APP_AUTH_URL' in process.env) ? process.env.VUE_APP_AUTH_URL : 'www.husleie.no/api/jwt';

		/** Add posibility to override API_BASE_URL of static built apps **/
		if ( window.localStorage.getItem('__OVERRIDE_API_BASE_URL') )
			this.baseUrl = window.localStorage.getItem('__OVERRIDE_API_BASE_URL');

		/** Add posibility to override AUTH_URL of static built apps **/
		if ( window.localStorage.getItem('__OVERRIDE_AUTH_URL') )
			this.authUrl = window.localStorage.getItem('__OVERRIDE_AUTH_URL');

		/** Normalize API key name **/
		/** @private */ this.apiKeyKey = apiKeyKeyPrefix + this.authUrl;
		this.apiKeyKey = this.apiKeyKey.replace(/[./]+/g, '_');
		this.apiKeyKey = this.apiKeyKey.replace(/\W/g, '');

		/** @private */ this.axios = axios.create({
			baseURL: this.baseUrl,
			withCredentials: true,
			transformRequest: [(data, headers) => {
				try {
					if ('localStorage' in window) {
						let token = window.localStorage.getItem(this.apiKeyKey);
						if (token) {
							headers['Authorization'] = token;
						}
					}
				} catch(e) {
					this.logger('error', 'In transformRequest', e);
				}

				// Force the request to be application/json
				headers['Content-Type'] = 'application/json;charset=utf-8';
				return JSON.stringify(data);
			}],
		});

		/** @private */ this.validate = validate;
	}

	/**
	 * Expands variables in a path containing {} templates
	 *
	 * @private
	 * @param {string} path The path string to be expanded
	 * @param {object} vals The object containing the values to be used when expanding
	 * @return {string} The expanded path
	 */
	expandPath(path, vals) {
		const regex = /\{(.*?)\}/;
		let match;
		while ((match = regex.exec(path)) !== null) {
			if (match[1] in vals && vals[match[1]] && vals[match[1]] !== '') {
				path = path.replace(match[0], vals[match[1]]);
			} else {
				let err = `Could not expand ${match[1]} in ${path}`;
				this.logger('error', err);
				throw new Error(err);
			}
		}
		return path;
	}

	/**
	 * Deauthorize the client by removing the cached APIKey. The client will try to reauthorize on the next request.
	 *
	 * @private
	 * @return {void}
	 */
	deauthorize() {
		try {
			if ('localStorage' in window) {
				window.localStorage.removeItem(this.apiKeyKey);
			}
		} catch(e) {
			this.logger('error', e);
		}
	}

	/**
	 * Asks auth server for a new API token
	 *
	 * @private
	 * @return {Promise}
	 */
	fetchAPIToken(token) {
		return new Promise((resolve, reject) => {
			axios.create({
				withCredentials: true,
			}).post(this.authUrl, token ? { token } : {}).then( res => {

				let token = 'Bearer ' + res.data.token;

				try {
					if ('localStorage' in window) {
						window.localStorage.setItem(this.apiKeyKey, token);
					}
				} catch(e) {
					this.logger('error', e);
				}

				resolve();

			}).catch( err => { reject(err); });
		});
	}

	/**
	 * Provides retry mechanism and handling of 401 responses from the API server
	 *
	 * @private
	 * @param {function} fn The function, returning a Promise, that should be retried.
	 * @param {number} retries The number of times the function should max be retried. Default 3.
	 * @param {object} err An error object. Should only be provided by the function itself.
	 * @return {Promise}
	 */
	retry(fn, retries=3, err=null) {

		if (!retries)
			return Promise.reject(err);

		return fn().catch(err => {
			if (err && err.response && (err.response.status == 401 || err.response.status == 403)) {
				this.logger('Got 401 from API, requesting new API token...');
				return this.fetchAPIToken().then( () => {
					this.logger('Got new token from API, retrying the original request.');
					return this.retry(fn, (retries-1), err);
				}).catch( err => {
					this.logger('error', 'Got error when asking for a new API token', err);
					return Promise.reject(err);
				});
			} else if (err && err.response && ( (err.response.status >= 500 && err.response.status <= 599) || err.response.status === '400' || err.response.status === '404' )) {
				this.logger('error', 'Got error for API server', err);
				return Promise.reject(err);
			} else if (err && err.isAxiosError) {
				this.logger('error', 'Got error from Axios:', err.toString());
				return Promise.reject(err);
			}

			this.logger('Retrying API request, ', (retries-1), ' retries remainding...');
			return this.retry(fn, (retries-1), err);
		});
	}

	/**
	 * Validates an object against a jsonschema
	 *
	 * @private
	 * @param {object} data The object to be validated
	 * @param {object} schema The schema that data should be validated against
	 * @return {boolean}
	 */
	validateSchema(data, schema) {
		const v = this.validate(data, schema);

		if (!v.valid) {
			this.logger('error', 'body does not validate:');
			for(let i = 0; i<v.errors.length; i++) {
				this.logger('error', ' -', v.errors[i].stack, v.errors[i]);
			}
		}

		return v.valid;
	}

	/**
	 * Provides a logger
	 *
	 * @private
	 * @param {...(boolean|number|string|object)} Any number of arguments to be logged on the same line. Objects will be copied, and logged as is at the time of logging.
	 */
	logger(...args) {
		if (!window.___api_logging)
			return;

		let level = 'info';

		const levels = {
			info: [ '%cAPI%c %c', 'color:blue;padding:2px 4px 2px 4px;border-radius:5px;background-color:#eef;border:.5px solid blue;', '', '' ],
			error: [ '%cAPI ERROR%c %c', 'color:red;padding:2px 4px 2px 4px;border-radius:5px;background-color:#fee;border:.5px solid red;', '', '' ],
			warn: [ '%cAPI WARN%c %c', 'color:orange;padding:2px 4px 2px 4px;border-radius:5px;background-color:#ffe;border:.5px solid orange;', '', '' ],
			debug: [ '%cAPI DEBUG%c %c', 'color:green;padding:2px 4px 2px 4px;border-radius:5px;background-color:#efe;border:.5px solid green;', '', '' ],
		};

		if (args[0] === 'error' || args[0] === 'warn' || args[0] === 'debug') {
			level = args.shift();
		}

		let template = [];
		for(let i=0;i<args.length;i++) {
			if (typeof(args[i]) === 'string' || typeof(args[i]) === 'undefined') {
				template.push('%s');
			} else if (typeof(args[i]) === 'number') {
				template.push( (Number(args[i]) === args[i] && args[i] % 1 !== 0) ? '%f' : '%d' );
			} else {
				args[i] = JSON.parse(JSON.stringify(args[i]));
				template.push('%O');
			}
		}
		// eslint-disable-next-line no-console
		console.log( levels[level].shift() + template.join(' '), ...levels[level], ...args);
	}

}


/**
 * Address
 * @extends SmartmieteAPI
 */
export class AddressApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Address"...');

	}


	/**
	 * Attempts to find the name of the area a postal code refers to in a country
	 * @param {} country The two-letter country code this postcode belongs to
	 * @param {} code The postcode we are looking up
	 * @see {@link file:///./redoc-husleieapi.html#operation/getPostalArea}
	 * @returns {Promise} A promise
	 */
	getPostalArea(country, code, responseType = 'json') {

		this.logger('Calling API function "getPostalArea" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/postalarea/{country}/{code}', {
			country,
			code,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Building
 * @extends SmartmieteAPI
 */
export class BuildingApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Building"...');

	}


	/**
	 * Create a new building and return the ID
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id Building ID
	 * @param {object} data.body.meta Building meta data
	 * @param {string} data.body.name Building name
	 * @param {string} data.body.type Building type Enum: ['unknown', 'apartment', 'house', 'apartment-building']

	 * @see {@link file:///./redoc-husleieapi.html#operation/createLandlordBuilding}
	 * @returns {Promise} A promise
	 */
	createLandlordBuilding(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createLandlordBuilding" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"id":{"description":"Building ID","readOnly":true,"type":"integer"},"meta":{"description":"Building meta data","type":"object"},"name":{"description":"Building name","type":"string"},"type":{"description":"Building type","enum":["unknown","apartment","house","apartment-building"],"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/buildings', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete a building
	 * @param {} landlord_id Required Landlord ID
	 * @param {} building_id Return only the building related to this building id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordBuilding}
	 * @returns {Promise} A promise
	 */
	deleteLandlordBuilding(landlord_id, building_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordBuilding" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/buildings/{building_id}', {
			landlord_id,
			building_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get all buildings for a landlord
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getAllLandlordBuildings}
	 * @returns {Promise} A promise
	 */
	getAllLandlordBuildings(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getAllLandlordBuildings" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/buildings', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a building
	 * @param {} landlord_id Required Landlord ID
	 * @param {} building_id Return only the building related to this building id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordBuilding}
	 * @returns {Promise} A promise
	 */
	getLandlordBuilding(landlord_id, building_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordBuilding" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/buildings/{building_id}', {
			landlord_id,
			building_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a building
	 * @param {} landlord_id Required Landlord ID
	 * @param {} building_id Return only the building related to this building id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id Building ID
	 * @param {object} data.body.meta Building meta data
	 * @param {string} data.body.name Building name
	 * @param {string} data.body.type Building type Enum: ['unknown', 'apartment', 'house', 'apartment-building']

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordBuilding}
	 * @returns {Promise} A promise
	 */
	patchLandlordBuilding(landlord_id, building_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordBuilding" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"id":{"description":"Building ID","readOnly":true,"type":"integer"},"meta":{"description":"Building meta data","type":"object"},"name":{"description":"Building name","type":"string"},"type":{"description":"Building type","enum":["unknown","apartment","house","apartment-building"],"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/buildings/{building_id}', {
			landlord_id,
			building_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a building
	 * @param {} landlord_id Required Landlord ID
	 * @param {} building_id Return only the building related to this building id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id Building ID
	 * @param {object} data.body.meta Building meta data
	 * @param {string} data.body.name Building name
	 * @param {string} data.body.type Building type Enum: ['unknown', 'apartment', 'house', 'apartment-building']

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordBuilding}
	 * @returns {Promise} A promise
	 */
	updateLandlordBuilding(landlord_id, building_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordBuilding" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"id":{"description":"Building ID","readOnly":true,"type":"integer"},"meta":{"description":"Building meta data","type":"object"},"name":{"description":"Building name","type":"string"},"type":{"description":"Building type","enum":["unknown","apartment","house","apartment-building"],"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/buildings/{building_id}', {
			landlord_id,
			building_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Chat
 * @extends SmartmieteAPI
 */
export class ChatApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Chat"...');

	}


	/**
	 * Adds feedback to a chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.rating 

	 * @see {@link file:///./redoc-husleieapi.html#operation/chatSessionFeedback}
	 * @returns {Promise} A promise
	 */
	chatSessionFeedback(landlord_id, chat_session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "chatSessionFeedback" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"rating":{"maximum":5,"minimum":1,"type":"number"}},"required":["rating"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}/feedback', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @see {@link file:///./redoc-husleieapi.html#operation/createChatSession}
	 * @returns {Promise} A promise
	 */
	createChatSession(landlord_id, responseType = 'json') {

		this.logger('Calling API function "createChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/chat', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Deletes a chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteChatSession}
	 * @returns {Promise} A promise
	 */
	deleteChatSession(landlord_id, chat_session_id, responseType = 'json') {

		this.logger('Calling API function "deleteChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a chat session object.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @see {@link file:///./redoc-husleieapi.html#operation/getChatSession}
	 * @returns {Promise} A promise
	 */
	getChatSession(landlord_id, chat_session_id, responseType = 'json') {

		this.logger('Calling API function "getChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Adds a message to a chat session from a user.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.message 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postMessageToChatSession}
	 * @returns {Promise} A promise
	 */
	postMessageToChatSession(landlord_id, chat_session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postMessageToChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"message":{"type":"string"}},"required":["message"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Updates the status of a component in a chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of a chat session.
	 * @param {} component_id The ID of a component.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.status  Enum: ['accepted', 'declined']

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateStatusForChatSessionComponent}
	 * @returns {Promise} A promise
	 */
	updateStatusForChatSessionComponent(landlord_id, chat_session_id, component_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateStatusForChatSessionComponent" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"status":{"enum":["accepted","declined"],"type":"string"}},"required":["status"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}/component/{component_id}', {
			landlord_id,
			chat_session_id,
			component_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Contract
 * @extends SmartmieteAPI
 */
export class ContractApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Contract"...');

	}


	/**
	 * Generates and offers the contract as a PDF-file for download. See download_token for generating the required token.
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.token {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/contract_download}
	 * @returns {Promise} A promise
	 */
	contract_download(contract_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "contract_download" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_session/{contract_id}/download', {
			contract_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a preview of the contract, based on the information in the contract session
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/contract_preview}
	 * @returns {Promise} A promise
	 */
	contract_preview(contract_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "contract_preview" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_session/{contract_id}/preview', {
			contract_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.extra 
	 * @param {string} data.body.plan 
	 * @param {string} data.body.product  Enum: ['rentadm', 'electricity_subscription', 'home_insurance', 'deposit_loan', 'deposit_account']

	 * @see {@link file:///./redoc-husleieapi.html#operation/contract_upsale}
	 * @returns {Promise} A promise
	 */
	contract_upsale(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "contract_upsale" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"extra":{"type":"object"},"plan":{"type":"string"},"product":{"enum":["rentadm","electricity_subscription","home_insurance","deposit_loan","deposit_account"],"type":"string"}},"required":["product"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}/upsale/', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new contract session object that the client can use to store contract session information.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.interest_management_id 
	 * @param {number} data.body.rental_object_id 
	 * @param {number} data.body.tenancy_id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createContractSession}
	 * @returns {Promise} A promise
	 */
	createContractSession({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"interest_management_id":{"type":"string"},"rental_object_id":{"type":"number"},"tenancy_id":{"type":"number"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/contract_session';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete a contract
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteContractSession}
	 * @returns {Promise} A promise
	 */
	deleteContractSession(contract_id, responseType = 'json') {

		this.logger('Calling API function "deleteContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/contract_session/{contract_id}', {
			contract_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Set the contract status to downloaded_for_offline_signing
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/downloadedForOfflineSigning}
	 * @returns {Promise} A promise
	 */
	downloadedForOfflineSigning(contract_id, responseType = 'json') {

		this.logger('Calling API function "downloadedForOfflineSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/contract_session/{contract_id}/downloaded_for_offline_signing', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the content of a contract session
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getContractSession}
	 * @returns {Promise} A promise
	 */
	getContractSession(contract_id, responseType = 'json') {

		this.logger('Calling API function "getContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_session/{contract_id}', {
			contract_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * This method will cancel the signing process and send the contract back to the landlord
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.message 

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendContractFeedback}
	 * @returns {Promise} A promise
	 */
	sendContractFeedback(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendContractFeedback" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"message":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}/feedback', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * This method will start the signing procedure by sending tenant a mail with a link for signing the contract
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 
	 * @param {object} data.body.callbacks 
	 * @param {object} data.body.callbacks.landlord 
	 * @param {string} data.body.callbacks.landlord.cancel 
	 * @param {string} data.body.callbacks.landlord.completed 
	 * @param {string} data.body.callbacks.landlord.error 
	 * @param {string} data.body.callbacks.landlord.postpone 
	 * @param {object} data.body.callbacks.tenant 
	 * @param {string} data.body.callbacks.tenant.cancel 
	 * @param {string} data.body.callbacks.tenant.completed 
	 * @param {string} data.body.callbacks.tenant.error 
	 * @param {string} data.body.callbacks.tenant.postpone 
	 * @param {string} data.body.signingMethod  Enum: ['bankid-no', 'canvas', 'offline', 'verified_digital_signing'] Default: [bankid-no]

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendContractForSigning}
	 * @returns {Promise} A promise
	 */
	sendContractForSigning(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendContractForSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"callbacks":{"properties":{"landlord":{"properties":{"cancel":{"type":"string"},"completed":{"type":"string"},"error":{"type":"string"},"postpone":{"type":"string"}},"required":["completed","postpone","cancel","error"],"type":"object"},"tenant":{"properties":{"cancel":{"type":"string"},"completed":{"type":"string"},"error":{"type":"string"},"postpone":{"type":"string"}},"required":["completed","postpone","cancel","error"],"type":"object"}},"required":["tenant","landlord"],"type":"object"},"signingMethod":{"default":"bankid-no","enum":["bankid-no","canvas","offline","verified_digital_signing"],"type":"string"}}};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}/send_for_signing', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a contract session, replacing the content of the contract session
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.dummy 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateContractSession}
	 * @returns {Promise} A promise
	 */
	updateContractSession(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"dummy":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a contract session, replacing the 'tenant' object, and the array of 'coTenants'
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.dummy 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateContractTenantDetails}
	 * @returns {Promise} A promise
	 */
	updateContractTenantDetails(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateContractTenantDetails" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"dummy":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}/tenant_details', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ContractSigning
 * @extends SmartmieteAPI
 */
export class ContractSigningApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ContractSigning"...');

	}


	/**
	 * Cancel a contract signing before landlord has signed
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/cancelContractSigning}
	 * @returns {Promise} A promise
	 */
	cancelContractSigning(contract_id, responseType = 'json') {

		this.logger('Calling API function "cancelContractSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/contract_signing/{contract_id}/cancel', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * The tenant has confirmed offline signing
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/confirmOfflineContractSigning}
	 * @returns {Promise} A promise
	 */
	confirmOfflineContractSigning(contract_id, responseType = 'json') {

		this.logger('Calling API function "confirmOfflineContractSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/contract_signing/{contract_id}/confirm_offline_signing', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the signing method used for this contract
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getContractPendingSignatures}
	 * @returns {Promise} A promise
	 */
	getContractPendingSignatures(contract_id, responseType = 'json') {

		this.logger('Calling API function "getContractPendingSignatures" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_signing/{contract_id}/pending_signatures', {
			contract_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the signing method used for this contract
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getContractSigningMethod}
	 * @returns {Promise} A promise
	 */
	getContractSigningMethod(contract_id, responseType = 'json') {

		this.logger('Calling API function "getContractSigningMethod" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_signing/{contract_id}/signing_method', {
			contract_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Prepare the QES signing for this contract
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/prepareContractQESSigning}
	 * @returns {Promise} A promise
	 */
	prepareContractQESSigning(contract_id, responseType = 'json') {

		this.logger('Calling API function "prepareContractQESSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_signing/{contract_id}/prepare_qes_signing', {
			contract_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Accepts a file token pointing to a signature, and marks the signature as complete.
	 * @param {} contract_id
	 * @param {} signature_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.signatureFileToken 

	 * @see {@link file:///./redoc-husleieapi.html#operation/signContract}
	 * @returns {Promise} A promise
	 */
	signContract(contract_id, signature_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "signContract" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"signatureFileToken":{"nullable":false,"type":"string"}},"required":["signatureFileToken"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_signing/{contract_id}/signature/{signature_id}', {
			contract_id,
			signature_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Electricity
 * @extends SmartmieteAPI
 */
export class ElectricityApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Electricity"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.amountOfResidents Number of residents
	 * @param {string} data.body.postalArea Usually City
	 * @param {number} data.body.postalCode Zip/Post code
	 * @param {string} data.body.street Street name
	 * @param {string} data.body.street2 Street name 2
	 * @param {string} data.body.usage Electricity usage in KWH

	 * @see {@link file:///./redoc-husleieapi.html#operation/calculateElectricityTariff}
	 * @returns {Promise} A promise
	 */
	calculateElectricityTariff({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "calculateElectricityTariff" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"amountOfResidents":{"description":"Number of residents","type":"number"},"postalArea":{"description":"Usually City","type":"string"},"postalCode":{"description":"Zip/Post code","maxLength":5,"minLength":5,"type":"number"},"street":{"description":"Street name","type":"string"},"street2":{"description":"Street name 2","nullable":true,"type":"string"},"usage":{"description":"Electricity usage in KWH","type":"string"}},"required":["postalCode","postalArea"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/product/electricity/calculate_tariff';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * IncomeExpense
 * @extends SmartmieteAPI
 */
export class IncomeExpenseApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "IncomeExpense"...');

	}


	/**
	 * Create a new income/expense and return the ID
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.allocationKeyId Optional allocation key ID for expenses/refunds
	 * @param {number} data.body.amount Amount
	 * @param {string} data.body.bookingDate Booking date
	 * @param {integer} data.body.buildingId Building id
	 * @param {integer} data.body.categoryId Category id
	 * @param {string} data.body.description Description
	 * @param {array} data.body.fileTokens 
	 * @param {integer} data.body.id IncomeExpense ID
	 * @param {string} data.body.intervalStart Invoice interval start date
	 * @param {string} data.body.invoiceInterval Invoice interval Enum: ['none', 'month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semiannual']
	 * @param {integer} data.body.rentalObjectId Rental object id
	 * @param {integer} data.body.tenancyId Tenancy id
	 * @param {string} data.body.type Income/Expense type Enum: ['income', 'expense', 'refund']
	 * @param {boolean} data.body.useInCalculation Use in utility cost calculation?

	 * @see {@link file:///./redoc-husleieapi.html#operation/createLandlordIncomesAndExpenses}
	 * @returns {Promise} A promise
	 */
	createLandlordIncomesAndExpenses(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createLandlordIncomesAndExpenses" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"allocationKeyId":{"description":"Optional allocation key ID for expenses/refunds","type":"number"},"amount":{"description":"Amount","format":"float","type":"number"},"bookingDate":{"description":"Booking date","format":"date","type":"string"},"buildingId":{"description":"Building id","nullable":true,"type":"integer"},"categoryId":{"description":"Category id","nullable":true,"type":"integer"},"description":{"description":"Description","type":"string"},"fileTokens":{"items":{"type":"string"},"type":"array"},"id":{"description":"IncomeExpense ID","readOnly":true,"type":"integer"},"intervalStart":{"description":"Invoice interval start date","format":"date","type":"string"},"invoiceInterval":{"description":"Invoice interval","enum":["none","month","bimonth","quarter","biquarter","annual","semiannual"],"type":"string"},"rentalObjectId":{"description":"Rental object id","nullable":true,"type":"integer"},"tenancyId":{"description":"Tenancy id","nullable":true,"type":"integer"},"type":{"description":"Income/Expense type","enum":["income","expense","refund"],"type":"string"},"useInCalculation":{"description":"Use in utility cost calculation?","type":"boolean"}},"required":["type","buildingId","rentalObjectId","categoryId","description","bookingDate","amount"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete an income/expense
	 * @param {} landlord_id Required Landlord ID
	 * @param {} income_expense_id Return only the income/expense related to this income_expense id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordIncomesAndExpenses}
	 * @returns {Promise} A promise
	 */
	deleteLandlordIncomesAndExpenses(landlord_id, income_expense_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordIncomesAndExpenses" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses/{income_expense_id}', {
			landlord_id,
			income_expense_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete one landlord income/expense file
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.incomeExpenseId Optional income/expense id the file belongs to
	 * @param {string} data.body.token File token ID

	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordIncomesAndExpensesFile}
	 * @returns {Promise} A promise
	 */
	deleteLandlordIncomesAndExpensesFile(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordIncomesAndExpensesFile" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"incomeExpenseId":{"description":"Optional income/expense id the file belongs to","type":"number"},"token":{"description":"File token ID","type":"string"}},"required":["token"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses/file', {
			landlord_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get all incomes and expenses for a landlord
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getAllLandlordIncomesAndExpenses}
	 * @returns {Promise} A promise
	 */
	getAllLandlordIncomesAndExpenses(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getAllLandlordIncomesAndExpenses" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a income/expense
	 * @param {} landlord_id Required Landlord ID
	 * @param {} income_expense_id Return only the income/expense related to this income_expense id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordIncomeExpense}
	 * @returns {Promise} A promise
	 */
	getLandlordIncomeExpense(landlord_id, income_expense_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordIncomeExpense" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses/{income_expense_id}', {
			landlord_id,
			income_expense_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get all incomes and expenses for a landlord and type
	 * @param {} landlord_id Required Landlord ID
	 * @param {} income_expense_type Required income and expense type
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordIncomesAndExpensesByType}
	 * @returns {Promise} A promise
	 */
	getLandlordIncomesAndExpensesByType(landlord_id, income_expense_type, responseType = 'json') {

		this.logger('Calling API function "getLandlordIncomesAndExpensesByType" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses/type/{income_expense_type}', {
			landlord_id,
			income_expense_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get income/expense categories
	 * @param {} income_expense_type Required income and expense type
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordIncomesAndExpensesCategories}
	 * @returns {Promise} A promise
	 */
	getLandlordIncomesAndExpensesCategories(income_expense_type, responseType = 'json') {

		this.logger('Calling API function "getLandlordIncomesAndExpensesCategories" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/incomes_and_expenses/categories/{income_expense_type}', {
			income_expense_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get income/expense form data
	 * @param {} income_expense_type Required income and expense type
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordIncomesAndExpensesFormData}
	 * @returns {Promise} A promise
	 */
	getLandlordIncomesAndExpensesFormData(income_expense_type, responseType = 'json') {

		this.logger('Calling API function "getLandlordIncomesAndExpensesFormData" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/incomes_and_expenses/form_data/{income_expense_type}', {
			income_expense_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update an income/expense
	 * @param {} landlord_id Required Landlord ID
	 * @param {} income_expense_id Return only the income/expense related to this income_expense id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.allocationKeyId Optional allocation key ID for expenses/refunds
	 * @param {number} data.body.amount Amount
	 * @param {string} data.body.bookingDate Booking date
	 * @param {integer} data.body.buildingId Building id
	 * @param {integer} data.body.categoryId Category id
	 * @param {string} data.body.description Description
	 * @param {array} data.body.fileTokens 
	 * @param {integer} data.body.id IncomeExpense ID
	 * @param {string} data.body.intervalStart Invoice interval start date
	 * @param {string} data.body.invoiceInterval Invoice interval Enum: ['none', 'month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semiannual']
	 * @param {integer} data.body.rentalObjectId Rental object id
	 * @param {integer} data.body.tenancyId Tenancy id
	 * @param {string} data.body.type Income/Expense type Enum: ['income', 'expense', 'refund']
	 * @param {boolean} data.body.useInCalculation Use in utility cost calculation?

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordIncomesAndExpenses}
	 * @returns {Promise} A promise
	 */
	updateLandlordIncomesAndExpenses(landlord_id, income_expense_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordIncomesAndExpenses" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"allocationKeyId":{"description":"Optional allocation key ID for expenses/refunds","type":"number"},"amount":{"description":"Amount","format":"float","type":"number"},"bookingDate":{"description":"Booking date","format":"date","type":"string"},"buildingId":{"description":"Building id","nullable":true,"type":"integer"},"categoryId":{"description":"Category id","nullable":true,"type":"integer"},"description":{"description":"Description","type":"string"},"fileTokens":{"items":{"type":"string"},"type":"array"},"id":{"description":"IncomeExpense ID","readOnly":true,"type":"integer"},"intervalStart":{"description":"Invoice interval start date","format":"date","type":"string"},"invoiceInterval":{"description":"Invoice interval","enum":["none","month","bimonth","quarter","biquarter","annual","semiannual"],"type":"string"},"rentalObjectId":{"description":"Rental object id","nullable":true,"type":"integer"},"tenancyId":{"description":"Tenancy id","nullable":true,"type":"integer"},"type":{"description":"Income/Expense type","enum":["income","expense","refund"],"type":"string"},"useInCalculation":{"description":"Use in utility cost calculation?","type":"boolean"}},"required":["type","buildingId","rentalObjectId","categoryId","description","bookingDate","amount"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/incomes_and_expenses/{income_expense_id}', {
			landlord_id,
			income_expense_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * LandlordAccounting
 * @extends SmartmieteAPI
 */
export class LandlordAccountingApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "LandlordAccounting"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {array} data.body.buildingIds 
	 * @param {integer} data.body.taxYear 

	 * @see {@link file:///./redoc-husleieapi.html#operation/generateAnlageVPDF}
	 * @returns {Promise} A promise
	 */
	generateAnlageVPDF(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "generateAnlageVPDF" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"buildingIds":{"items":{"type":"integer"},"minItems":1,"type":"array","uniqueItems":true},"taxYear":{"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/accounting/generate_anlage_v_pdf', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.fromDate {}From date
	 * @param data.query.toDate {}To date
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordAccountingOverview}
	 * @returns {Promise} A promise
	 */
	getLandlordAccountingOverview(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordAccountingOverview" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/overview', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.year {}Return only rent tracker invoice within specified year
	 * @param data.query.tenancyId {}Return only rent tracker invoices with this tenancy id
	 * @param data.query.rentalObjectId {}Return only rent tracker invoices with this rental object id
	 * @param data.query.buildingId {}Return only rent tracker invoices with this building id
	 * @param data.query.status {}Return only rent tracker invoices with this status
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordRentTrackerOverview}
	 * @returns {Promise} A promise
	 */
	getLandlordRentTrackerOverview(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordRentTrackerOverview" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/rent_tracker/overview', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Payment
 * @extends SmartmieteAPI
 */
export class PaymentApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Payment"...');

	}


	/**
	 * Returns the stripe public and setup intent secret
	 * @see {@link file:///./redoc-husleieapi.html#operation/CreateSetupIntent}
	 * @returns {Promise} A promise
	 */
	CreateSetupIntent(responseType = 'json') {

		this.logger('Calling API function "CreateSetupIntent" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/payment/create_setup_intent';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ProfilesLandlord
 * @extends SmartmieteAPI
 */
export class ProfilesLandlordApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ProfilesLandlord"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.acknowledgementToken UUID token for acknowledging a verification
	 * @param {array} data.body.addresses 
	 * @param {string} data.body.addresses[].careOf If postal and C/O aka care of is required
	 * @param {string} data.body.addresses[].countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.addresses[].postalArea Postal area
	 * @param {string} data.body.addresses[].postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.addresses[].postalZipcode Postal Zipcode
	 * @param {string} data.body.addresses[].street Street name and number
	 * @param {string} data.body.addresses[].street2 Secondary street name and number
	 * @param {integer} data.body.id 
	 * @param {object} data.body.meta 
	 * @param {object} data.body.meta.needsAcknowledgement 
	 * @param {integer} data.body.meta.needsAcknowledgement.id 
	 * @param {string} data.body.meta.needsAcknowledgement.landlordName 
	 * @param {object} data.body.meta.needsAcknowledgement.nationalIdentityNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.previousValue 
	 * @param {object} data.body.meta.needsAcknowledgement.organizationNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.previousValue 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects 
	 * @param {integer} data.body.meta.needsAcknowledgement.rentalObjects[].id 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects[].issues 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].error 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].name 
	 * @param {} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].previousValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].name 
	 * @param {string} data.body.meta.needsAcknowledgement.token UUID token for tracking verification acknowledgements
	 * @param {string} data.body.name 
	 * @param {object} data.body.owner 
	 * @param {integer} data.body.owner.id 
	 * @param {string} data.body.owner.name 
	 * @param {string} data.body.profilePicture 
	 * @param {object} data.body.stats 
	 * @param {integer} data.body.stats.rentalObjectCount  Default: [0]
	 * @param {integer} data.body.stats.tenancyCount  Default: [0]

	 * @see {@link file:///./redoc-husleieapi.html#operation/createProfileLandlord}
	 * @returns {Promise} A promise
	 */
	createProfileLandlord({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"acknowledgementToken":{"description":"UUID token for acknowledging a verification","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"addresses":{"items":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"type":"array"},"id":{"readOnly":true,"type":"integer"},"meta":{"properties":{"needsAcknowledgement":{"properties":{"id":{"type":"integer"},"landlordName":{"type":"string"},"nationalIdentityNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"organizationNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"rentalObjects":{"items":{"properties":{"id":{"type":"integer"},"issues":{"items":{"properties":{"error":{"type":"string"},"name":{"type":"string"},"newValue":{"oneOf":[{"nullable":true,"type":"string"},{"items":{"type":"string"},"type":"array"}]},"previousValue":{"nullable":true,"type":"string"}},"required":["name","error","newValue"],"title":"rentalObjectAcknowledgementField","type":"object"},"type":"array"},"name":{"type":"string"}},"required":["id"],"title":"rentalObjectAcknowledgement","type":"object"},"type":"array"},"token":{"description":"UUID token for tracking verification acknowledgements","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"}},"title":"landlordAcknowledgement","type":"object"}},"readOnly":true,"type":"object"},"name":{"type":"string"},"owner":{"properties":{"id":{"type":"integer"},"name":{"type":"string"}},"readOnly":true,"type":"object"},"profilePicture":{"readOnly":true,"type":"string"},"stats":{"properties":{"rentalObjectCount":{"default":"0","minimum":0,"type":"integer"},"tenancyCount":{"default":"0","minimum":0,"type":"integer"}},"readOnly":true,"type":"object"}},"title":"ProfileLandlord","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/landlords';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteProfileLandlord}
	 * @returns {Promise} A promise
	 */
	deleteProfileLandlord(landlord_id, responseType = 'json') {

		this.logger('Calling API function "deleteProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/profiles/landlords/{landlord_id}', {
			landlord_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlord}
	 * @returns {Promise} A promise
	 */
	getProfileLandlord(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @see {@link file:///./redoc-husleieapi.html#operation/listProfileLandlords}
	 * @returns {Promise} A promise
	 */
	listProfileLandlords({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "listProfileLandlords" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/landlords';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.acknowledgementToken UUID token for acknowledging a verification
	 * @param {array} data.body.addresses 
	 * @param {string} data.body.addresses[].careOf If postal and C/O aka care of is required
	 * @param {string} data.body.addresses[].countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.addresses[].postalArea Postal area
	 * @param {string} data.body.addresses[].postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.addresses[].postalZipcode Postal Zipcode
	 * @param {string} data.body.addresses[].street Street name and number
	 * @param {string} data.body.addresses[].street2 Secondary street name and number
	 * @param {integer} data.body.id 
	 * @param {object} data.body.meta 
	 * @param {object} data.body.meta.needsAcknowledgement 
	 * @param {integer} data.body.meta.needsAcknowledgement.id 
	 * @param {string} data.body.meta.needsAcknowledgement.landlordName 
	 * @param {object} data.body.meta.needsAcknowledgement.nationalIdentityNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.previousValue 
	 * @param {object} data.body.meta.needsAcknowledgement.organizationNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.previousValue 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects 
	 * @param {integer} data.body.meta.needsAcknowledgement.rentalObjects[].id 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects[].issues 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].error 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].name 
	 * @param {} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].previousValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].name 
	 * @param {string} data.body.meta.needsAcknowledgement.token UUID token for tracking verification acknowledgements
	 * @param {string} data.body.name 
	 * @param {object} data.body.owner 
	 * @param {integer} data.body.owner.id 
	 * @param {string} data.body.owner.name 
	 * @param {string} data.body.profilePicture 
	 * @param {object} data.body.stats 
	 * @param {integer} data.body.stats.rentalObjectCount  Default: [0]
	 * @param {integer} data.body.stats.tenancyCount  Default: [0]

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchProfileLandlord}
	 * @returns {Promise} A promise
	 */
	patchProfileLandlord(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"acknowledgementToken":{"description":"UUID token for acknowledging a verification","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"addresses":{"items":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"type":"array"},"id":{"readOnly":true,"type":"integer"},"meta":{"properties":{"needsAcknowledgement":{"properties":{"id":{"type":"integer"},"landlordName":{"type":"string"},"nationalIdentityNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"organizationNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"rentalObjects":{"items":{"properties":{"id":{"type":"integer"},"issues":{"items":{"properties":{"error":{"type":"string"},"name":{"type":"string"},"newValue":{"oneOf":[{"nullable":true,"type":"string"},{"items":{"type":"string"},"type":"array"}]},"previousValue":{"nullable":true,"type":"string"}},"required":["name","error","newValue"],"title":"rentalObjectAcknowledgementField","type":"object"},"type":"array"},"name":{"type":"string"}},"required":["id"],"title":"rentalObjectAcknowledgement","type":"object"},"type":"array"},"token":{"description":"UUID token for tracking verification acknowledgements","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"}},"title":"landlordAcknowledgement","type":"object"}},"readOnly":true,"type":"object"},"name":{"type":"string"},"owner":{"properties":{"id":{"type":"integer"},"name":{"type":"string"}},"readOnly":true,"type":"object"},"profilePicture":{"readOnly":true,"type":"string"},"stats":{"properties":{"rentalObjectCount":{"default":"0","minimum":0,"type":"integer"},"tenancyCount":{"default":"0","minimum":0,"type":"integer"}},"readOnly":true,"type":"object"}},"title":"ProfileLandlord","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}', {
			landlord_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ProfilesUser
 * @extends SmartmieteAPI
 */
export class ProfilesUserApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ProfilesUser"...');

	}


	/**
	 * Apply promo code to existing subscription
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.promoCode Promo code for a coupon

	 * @see {@link file:///./redoc-husleieapi.html#operation/applyStripePromocode}
	 * @returns {Promise} A promise
	 */
	applyStripePromocode({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "applyStripePromocode" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"promoCode":{"description":"Promo code for a coupon","type":"string"}},"required":["promoCode"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/subscription/apply_promocode';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Cancel subscription
	 * @see {@link file:///./redoc-husleieapi.html#operation/cancelProfileUserSubscription}
	 * @returns {Promise} A promise
	 */
	cancelProfileUserSubscription(responseType = 'json') {

		this.logger('Calling API function "cancelProfileUserSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = '/profiles/user/subscription/cancel';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Check promo code
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.promoCode Promo code for a coupon

	 * @see {@link file:///./redoc-husleieapi.html#operation/checkStripePromocode}
	 * @returns {Promise} A promise
	 */
	checkStripePromocode({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "checkStripePromocode" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"promoCode":{"description":"Promo code for a coupon","type":"string"}},"required":["promoCode"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/subscription/check_promocode';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Create billing portal session
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.returnUrl 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createProfileUserBillingPortalSession}
	 * @returns {Promise} A promise
	 */
	createProfileUserBillingPortalSession({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createProfileUserBillingPortalSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"returnUrl":{"type":"string"}},"required":["returnUrl"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/billing_portal';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Create checkout session
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.cancelUrl Relative path to page to return to if checkout is canceled
	 * @param {string} data.body.origin Origin of this call
	 * @param {string} data.body.successUrl Relative path to page to return to if checkout is successful

	 * @see {@link file:///./redoc-husleieapi.html#operation/createProfileUserCheckoutSession}
	 * @returns {Promise} A promise
	 */
	createProfileUserCheckoutSession({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createProfileUserCheckoutSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"cancelUrl":{"description":"Relative path to page to return to if checkout is canceled","type":"string"},"origin":{"description":"Origin of this call","type":"string"},"successUrl":{"description":"Relative path to page to return to if checkout is successful","type":"string"}},"required":["successUrl","cancelUrl"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/checkout_session';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Create subscription
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.origin Origin of this call
	 * @param {string} data.body.promoCode Optional promotional code for a coupon
	 * @param {string} data.body.subscriptionType Is it a monthly or yearly subscription? Enum: ['month', 'annual']

	 * @see {@link file:///./redoc-husleieapi.html#operation/createProfileUserSubscription}
	 * @returns {Promise} A promise
	 */
	createProfileUserSubscription({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createProfileUserSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"origin":{"description":"Origin of this call","type":"string"},"promoCode":{"description":"Optional promotional code for a coupon","type":"string"},"subscriptionType":{"description":"Is it a monthly or yearly subscription?","enum":["month","annual"],"type":"string"}},"required":["subscriptionType"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/subscription';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUser}
	 * @returns {Promise} A promise
	 */
	getProfileUser(responseType = 'json') {

		this.logger('Calling API function "getProfileUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} token
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserPasswordToken}
	 * @returns {Promise} A promise
	 */
	getProfileUserPasswordToken(token, responseType = 'json') {

		this.logger('Calling API function "getProfileUserPasswordToken" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/user/password/token/{token}', {
			token,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get subscription status
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserSubscription}
	 * @returns {Promise} A promise
	 */
	getProfileUserSubscription(responseType = 'json') {

		this.logger('Calling API function "getProfileUserSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user/subscription';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserWhoAmI}
	 * @returns {Promise} A promise
	 */
	getProfileUserWhoAmI(responseType = 'json') {

		this.logger('Calling API function "getProfileUserWhoAmI" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user/whoami';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.cellPhone Users cell phone. See the verified object, and seperate API-methods for performing the validation.
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email Unique email. Updating this field can fail if there is already a user with this email in the system. See the verified object, and seperate API-methods for performing the validation.
	 * @param {string} data.body.familyName 
	 * @param {string} data.body.givenName 
	 * @param {object} data.body.title 
	 * @param {string} data.body.title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.title.professional From English honorifics, academic and professional, Dr, Professor etc Enum: ['PROF', 'DR', 'PROF_DR']
	 * @param {array} data.body.userRoles 
	 * @param {object} data.body.verified This object contains keys that will be defined with a date to indicate when the verification was completed. This field is read-only.
	 * @param {string} data.body.verified.cellPhone This field is read-only.
	 * @param {string} data.body.verified.email This field is read-only.

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchProfileUser}
	 * @returns {Promise} A promise
	 */
	patchProfileUser({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchProfileUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"cellPhone":{"description":"Users cell phone. See the verified object, and seperate API-methods for performing the validation.","maxLength":20,"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","type":"string"},"email":{"description":"Unique email. Updating this field can fail if there is already a user with this email in the system. See the verified object, and seperate API-methods for performing the validation.","format":"email","maxLength":255,"type":"string"},"familyName":{"nullable":true,"type":"string"},"givenName":{"nullable":true,"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"},"professional":{"description":"From English honorifics, academic and professional, Dr, Professor etc","enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"},"userRoles":{"items":{"enum":["tenant","landlord","administrator","partner_api"],"type":"string"},"readOnly":true,"type":"array"},"verified":{"description":"This object contains keys that will be defined with a date to indicate when the verification was completed. This field is read-only.","properties":{"cellPhone":{"description":"This field is read-only.","format":"date-time","type":"string"},"email":{"description":"This field is read-only.","format":"date-time","type":"string"}},"readOnly":true,"type":"object"}},"title":"ProfileUser","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user';

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.context The context property is used to choose which email template to use when resending the verification mail. Enum: ['signup', 'profile', 'reverify'] Default: [profile]
	 * @param {string} data.body.email If user has provided a new email, the user profile will be updated with the new email, and a new mail verification mail will be sent.

	 * @see {@link file:///./redoc-husleieapi.html#operation/profileUserTriggerNewEmailVerificationEmail}
	 * @returns {Promise} A promise
	 */
	profileUserTriggerNewEmailVerificationEmail({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "profileUserTriggerNewEmailVerificationEmail" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"context":{"default":"profile","description":"The context property is used to choose which email template to use when resending the verification mail.","enum":["signup","profile","reverify"],"type":"string"},"email":{"description":"If user has provided a new email, the user profile will be updated with the new email, and a new mail verification mail will be sent.","format":"email","maxLength":255,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/verify/email/trigger';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.token 

	 * @see {@link file:///./redoc-husleieapi.html#operation/profileUserVerifyEmail}
	 * @returns {Promise} A promise
	 */
	profileUserVerifyEmail({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "profileUserVerifyEmail" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"token":{"maxLength":36,"minLength":36,"type":"string"}},"required":["token"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/verify/email';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Resume subscription
	 * @see {@link file:///./redoc-husleieapi.html#operation/resumeProfileUserSubscription}
	 * @returns {Promise} A promise
	 */
	resumeProfileUserSubscription(responseType = 'json') {

		this.logger('Calling API function "resumeProfileUserSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = '/profiles/user/subscription/resume';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.currentPassword 
	 * @param {string} data.body.newPassword 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileUserPassword}
	 * @returns {Promise} A promise
	 */
	setProfileUserPassword({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileUserPassword" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"currentPassword":{"type":"string"},"newPassword":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/password';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileUserPreference}
	 * @returns {Promise} A promise
	 */
	setProfileUserPreference({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileUserPreference" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"properties":{"key":{"nullable":false,"type":"string"},"value":{"nullable":true,"type":"string"}},"title":"KeyValueObject","type":"object"},{"items":{"properties":{"key":{"nullable":false,"type":"string"},"value":{"nullable":true,"type":"string"}},"title":"KeyValueObject","type":"object"},"title":"KeyValueObjectArray","type":"array"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/preference';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Start auto-renewal of subscription
	 * @see {@link file:///./redoc-husleieapi.html#operation/startAutoRenewalProfileUserSubscription}
	 * @returns {Promise} A promise
	 */
	startAutoRenewalProfileUserSubscription(responseType = 'json') {

		this.logger('Calling API function "startAutoRenewalProfileUserSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = '/profiles/user/subscription/start_auto_renewal';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Stop auto-renewal of subscription
	 * @see {@link file:///./redoc-husleieapi.html#operation/stopAutoRenewalProfileUserSubscription}
	 * @returns {Promise} A promise
	 */
	stopAutoRenewalProfileUserSubscription(responseType = 'json') {

		this.logger('Calling API function "stopAutoRenewalProfileUserSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = '/profiles/user/subscription/stop_auto_renewal';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * QA
 * @extends SmartmieteAPI
 */
export class QAApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "QA"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.lastUpdate Search for mandrill logs after the last update date
	 * @param {integer} data.body.maxRows Max rows to return Default: [10]
	 * @param {string} data.body.receiver Email address of the receiver
	 * @param {string} data.body.template Search for mandrill logs that match a template
	 * @param {object} data.body.vars search for vars object

	 * @see {@link file:///./redoc-husleieapi.html#operation/qaSearchMandrillLogs}
	 * @returns {Promise} A promise
	 */
	qaSearchMandrillLogs({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "qaSearchMandrillLogs" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"example":{"lastUpdate":"2022-12-01T12:01:01","maxRows":100,"receiver":"example@email.com","template":"template_name","vars":{"landlord_id":50}},"properties":{"lastUpdate":{"description":"Search for mandrill logs after the last update date","format":"date-time","type":"string"},"maxRows":{"default":"10","description":"Max rows to return","type":"integer"},"receiver":{"description":"Email address of the receiver","type":"string"},"template":{"description":"Search for mandrill logs that match a template","type":"string"},"vars":{"description":"search for vars object","type":"object"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/qa/search_mandrill_logs';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/qaSession}
	 * @returns {Promise} A promise
	 */
	qaSession(session_id, responseType = 'json') {

		this.logger('Calling API function "qaSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/qa/session/{session_id}', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.created Create time
	 * @param {string} data.body.id ID of the Session
	 * @param {object} data.body.meta Meta data
	 * @param {string} data.body.modified Last modified time
	 * @param {object} data.body.object Object data
	 * @param {string} data.body.type Type of session store

	 * @see {@link file:///./redoc-husleieapi.html#operation/qaUpdateSession}
	 * @returns {Promise} A promise
	 */
	qaUpdateSession(session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "qaUpdateSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"created":{"description":"Create time","nullable":false,"type":"string"},"id":{"description":"ID of the Session","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"meta":{"description":"Meta data","nullable":true,"type":"object"},"modified":{"description":"Last modified time","nullable":false,"type":"string"},"object":{"description":"Object data","nullable":true,"type":"object"},"type":{"description":"Type of session store","nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/qa/session/{session_id}', {
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * RentalObject
 * @extends SmartmieteAPI
 */
export class RentalObjectApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "RentalObject"...');

	}


	/**
	 * Remove one rental object
	 * @param {} landlord_id Required Landlord ID
	 * @param {} rental_object_id Required Rental Object ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	deleteLandlordRentalObject(landlord_id, rental_object_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/rental_objects/{rental_object_id}', {
			landlord_id,
			rental_object_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a specific rental objects and attached tenancies
	 * @param {} landlord_id Required Landlord ID
	 * @param {} rental_object_id Required Rental Object ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.tenancyStatus {}Which status of the tenancies should be listed
	 * @param data.query.extended {}Return extended information about the rental object
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	getLandlordRentalObject(landlord_id, rental_object_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/rental_objects/{rental_object_id}', {
			landlord_id,
			rental_object_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * List rental objects for specified landlord and attached tenancies
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordRentalObjects}
	 * @returns {Promise} A promise
	 */
	getLandlordRentalObjects(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordRentalObjects" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/rental_objects', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a specific rental objects and attached tenancies
	 * @param {} landlord_id Required Landlord ID
	 * @param {} rental_object_id Required Rental Object ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.alias 
	 * @param {string} data.body.apartmentLocation 
	 * @param {integer} data.body.belongsToBuildingId 
	 * @param {string} data.body.countryCode 
	 * @param {string} data.body.dateOfConstruction 
	 * @param {boolean} data.body.deleted 
	 * @param {string} data.body.electricityMeterNumber 
	 * @param {integer} data.body.id 
	 * @param {integer} data.body.landlordId 
	 * @param {array} data.body.livingAreas 
	 * @param {number} data.body.livingAreasNumber 
	 * @param {string} data.body.postalArea 
	 * @param {string} data.body.postalZipcode 
	 * @param {number} data.body.squareMeters 
	 * @param {string} data.body.street 
	 * @param {string} data.body.street2 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	patchLandlordRentalObject(landlord_id, rental_object_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"alias":{"nullable":true,"type":"string"},"apartmentLocation":{"nullable":true,"type":"string"},"belongsToBuildingId":{"nullable":true,"type":"integer"},"countryCode":{"nullable":false,"pattern":"^DE$","type":"string"},"dateOfConstruction":{"nullable":true,"type":"string"},"deleted":{"type":"boolean"},"electricityMeterNumber":{"nullable":true,"type":"string"},"id":{"type":"integer"},"landlordId":{"type":"integer"},"livingAreas":{"items":{"type":"object"},"nullable":true,"type":"array"},"livingAreasNumber":{"nullable":true,"type":"number"},"postalArea":{"nullable":true,"type":"string"},"postalZipcode":{"nullable":true,"type":"string"},"squareMeters":{"nullable":true,"type":"number"},"street":{"nullable":false,"type":"string"},"street2":{"nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/rental_objects/{rental_object_id}', {
			landlord_id,
			rental_object_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new rental object in the database and returns the id of the new object
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.alias 
	 * @param {string} data.body.apartmentLocation 
	 * @param {integer} data.body.belongsToBuildingId 
	 * @param {string} data.body.countryCode 
	 * @param {string} data.body.dateOfConstruction 
	 * @param {string} data.body.electricityMeterNumber 
	 * @param {array} data.body.livingAreas 
	 * @param {number} data.body.livingAreasNumber 
	 * @param {string} data.body.postalArea 
	 * @param {string} data.body.postalZipcode 
	 * @param {number} data.body.squareMeters 
	 * @param {string} data.body.street 
	 * @param {string} data.body.street2 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	postLandlordRentalObject(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"alias":{"nullable":true,"type":"string"},"apartmentLocation":{"nullable":true,"type":"string"},"belongsToBuildingId":{"nullable":true,"type":"integer"},"countryCode":{"nullable":false,"pattern":"^DE$","type":"string"},"dateOfConstruction":{"nullable":true,"type":"string"},"electricityMeterNumber":{"nullable":true,"type":"string"},"livingAreas":{"items":{"type":"object"},"nullable":true,"type":"array"},"livingAreasNumber":{"nullable":true,"type":"number"},"postalArea":{"nullable":true,"type":"string"},"postalZipcode":{"nullable":true,"type":"string"},"squareMeters":{"nullable":true,"type":"number"},"street":{"nullable":false,"type":"string"},"street2":{"nullable":true,"type":"string"}},"required":["countryCode"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/rental_objects', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Service
 * @extends SmartmieteAPI
 */
export class ServiceApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Service"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.filename Filename of PDF
	 * @param {string} data.body.html HTML to render
	 * @param {integer} data.body.rental_object_id Rental Object ID to upload PDF to
	 * @param {integer} data.body.tenancy_id Tenancy ID to upload PDF to

	 * @see {@link file:///./redoc-husleieapi.html#operation/renderPDF}
	 * @returns {Promise} A promise
	 */
	renderPDF({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "renderPDF" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"example":{"filename":"document.pdf","html":"<html><body><h1>Hello World</h1></body></html>","tenancy_id":123},"properties":{"filename":{"description":"Filename of PDF","nullable":true,"type":"string"},"html":{"description":"HTML to render","nullable":false,"type":"string"},"rental_object_id":{"description":"Rental Object ID to upload PDF to","nullable":true,"type":"integer"},"tenancy_id":{"description":"Tenancy ID to upload PDF to","nullable":true,"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/services/pdf/render';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * SessionStore
 * @extends SmartmieteAPI
 */
export class SessionStoreApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "SessionStore"...');

	}


	/**
	 * Returns a list of possible landlord upsell opportunities for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/LandlordUpsellOpportunities}
	 * @returns {Promise} A promise
	 */
	LandlordUpsellOpportunities(type, session_id, responseType = 'json') {

		this.logger('Calling API function "LandlordUpsellOpportunities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/landlord/opportunities', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returned prepared landlord upsell data for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @see {@link file:///./redoc-husleieapi.html#operation/PrepareLandlordUpsell}
	 * @returns {Promise} A promise
	 */
	PrepareLandlordUpsell(type, session_id, upsell_type, responseType = 'json') {

		this.logger('Calling API function "PrepareLandlordUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/landlord/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returned prepared tenant upsell data for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @see {@link file:///./redoc-husleieapi.html#operation/PrepareTenantUpsell}
	 * @returns {Promise} A promise
	 */
	PrepareTenantUpsell(type, session_id, upsell_type, responseType = 'json') {

		this.logger('Calling API function "PrepareTenantUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/tenant/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a list of possible tenant upsell opportunities for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/TenantUpsellOpportunities}
	 * @returns {Promise} A promise
	 */
	TenantUpsellOpportunities(type, session_id, responseType = 'json') {

		this.logger('Calling API function "TenantUpsellOpportunities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/tenant/opportunities', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new session object with role {type} that the client can use to store the session information.
	 * @param {} type Required session type
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createSession}
	 * @returns {Promise} A promise
	 */
	createSession(type, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"properties":{"session_id":{"type":"string"}},"required":["session_id"],"type":"object"},{"properties":{"tenancy_id":{"type":"string"}},"required":["tenancy_id"],"type":"object"},{"properties":{"rental_object_id":{"type":"string"}},"required":["rental_object_id"],"type":"object"},{"properties":{"landlord_id":{"type":"string"}},"required":["landlord_id"],"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}', {
			type,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Decision of tenant upsell opportunity
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.decision  Enum: ['accepted', 'declined']
	 * @param {string} data.body.upsell_id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/decisionTenantUpsell}
	 * @returns {Promise} A promise
	 */
	decisionTenantUpsell(type, session_id, upsell_type, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "decisionTenantUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"decision":{"enum":["accepted","declined"],"type":"string"},"upsell_id":{"nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/upsell/tenant/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete a session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteSession}
	 * @returns {Promise} A promise
	 */
	deleteSession(type, session_id, responseType = 'json') {

		this.logger('Calling API function "deleteSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/session/{type}/{session_id}', {
			type,
			session_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Generates and offers the session as a HTML- or PDF-file for download. See download_token for generating the required token.
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.token {}
	 * @param data.query.format {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/downloadSession}
	 * @returns {Promise} A promise
	 */
	downloadSession(type, session_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "downloadSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/download', {
			type,
			session_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns an object with a token that can be used to download a PDF-version of the session. A url that can be used directly is also provided.
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDownloadTokenByJWT}
	 * @returns {Promise} A promise
	 */
	getDownloadTokenByJWT(type, session_id, responseType = 'json') {

		this.logger('Calling API function "getDownloadTokenByJWT" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/download_token', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns an object with a token that can be used to download a PDF-version of the session. A url that can be used directly is also provided.
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.pin 

	 * @see {@link file:///./redoc-husleieapi.html#operation/getDownloadTokenByPIN}
	 * @returns {Promise} A promise
	 */
	getDownloadTokenByPIN(type, session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "getDownloadTokenByPIN" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"pin":{"type":"string"}},"required":["pin"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/download_token', {
			type,
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the content of a session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSession}
	 * @returns {Promise} A promise
	 */
	getSession(type, session_id, responseType = 'json') {

		this.logger('Calling API function "getSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the signing method available for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSigningMethods}
	 * @returns {Promise} A promise
	 */
	getSigningMethods(type, session_id, responseType = 'json') {

		this.logger('Calling API function "getSigningMethods" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/signing_methods', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Handle a session callback
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {array} data.body.nextSigners 
	 * @param {object} data.body.nextSigners[].details 
	 * @param {string} data.body.nextSigners[].details.errorRedirectURL Might be used by the signing method implementation to redirect the user after a failed signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.
	 * @param {string} data.body.nextSigners[].details.successRedirectURL Might be used by the signing method implementation to redirect the user after a successful signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.
	 * @param {string} data.body.nextSigners[].id 
	 * @param {object} data.body.nextSigners[].signer 
	 * @param {object} data.body.nextSigners[].signer.address 
	 * @param {string} data.body.nextSigners[].signer.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.nextSigners[].signer.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.nextSigners[].signer.address.postalArea Postal area
	 * @param {string} data.body.nextSigners[].signer.address.postalCode Postal Zipcode
	 * @param {string} data.body.nextSigners[].signer.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.nextSigners[].signer.address.street Street name and number
	 * @param {string} data.body.nextSigners[].signer.address.street2 Secondary street name and number
	 * @param {string} data.body.nextSigners[].signer.cellPhone 
	 * @param {string} data.body.nextSigners[].signer.dateOfBirth The entity date of birth, in ISO-8601 format
	 * @param {string} data.body.nextSigners[].signer.email 
	 * @param {string} data.body.nextSigners[].signer.familyName 
	 * @param {string} data.body.nextSigners[].signer.givenName 
	 * @param {string} data.body.nextSigners[].signer.placeOfBirth The entity place of birth
	 * @param {object} data.body.nextSigners[].signer.title 
	 * @param {string} data.body.nextSigners[].signer.title.common  Enum: ['DIVERSE', 'MSS', 'MR']
	 * @param {string} data.body.nextSigners[].signer.title.professional  Enum: ['PROF', 'DR', 'PROF_DR']
	 * @param {string} data.body.nextSigners[].status  Enum: ['waiting', 'pending', 'signed', 'declined']
	 * @param {string} data.body.nextSigners[].type  Enum: ['landlord', 'tenant', 'co-tenant', 'bail', 'witness']
	 * @param {string} data.body.signedToken 
	 * @param {string} data.body.signingSessionId 
	 * @param {string} data.body.step  Enum: ['create', 'add_signature', 'finalize', 'delete']

	 * @param data.query {object}
	 * @param data.query.token {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/handleSessionCallback}
	 * @returns {Promise} A promise
	 */
	handleSessionCallback(type, session_id, { body, query } = {}, responseType = 'json') {

		this.logger('Calling API function "handleSessionCallback" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"nextSigners":{"items":{"additionalProperties":false,"properties":{"details":{"properties":{"errorRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a failed signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"},"successRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a successful signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"}},"type":"object"},"id":{"type":"string"},"signer":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":false,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","email"],"type":"object"},"status":{"enum":["waiting","pending","signed","declined"],"type":"string"},"type":{"enum":["landlord","tenant","co-tenant","bail","witness"],"type":"string"}},"required":["type","signer"],"type":"object"},"type":"array"},"signedToken":{"type":"string"},"signingSessionId":{"type":"string"},"step":{"enum":["create","add_signature","finalize","delete"],"type":"string"}},"required":["signingSessionId","step","nextSigners"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/callback', {
			type,
			session_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the necessary data to create a signing session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.signingMethod  Enum: ['offline', 'token', 'verified_digital_signing']

	 * @see {@link file:///./redoc-husleieapi.html#operation/prepareSigning}
	 * @returns {Promise} A promise
	 */
	prepareSigning(type, session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "prepareSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"signingMethod":{"enum":["offline","token","verified_digital_signing"],"type":"string"}},"required":["signingMethod"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/prepare_signing', {
			type,
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returned prepared tenant upsell data for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @see {@link file:///./redoc-husleieapi.html#operation/prepareUpsell}
	 * @returns {Promise} A promise
	 */
	prepareUpsell(type, session_id, upsell_type, responseType = 'json') {

		this.logger('Calling API function "prepareUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Generates and offers the session as a HTML-, PDF- or JSON-file for preview
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/previewSession}
	 * @returns {Promise} A promise
	 */
	previewSession(type, session_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "previewSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/preview', {
			type,
			session_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a session, replacing the content of the session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateSession}
	 * @returns {Promise} A promise
	 */
	updateSession(type, session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"properties":{"coTenants":{"items":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":false,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","email"],"type":"object"},"type":"array"},"inspectionReport":{"properties":{"comment":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"}},"type":"object"},"landlord":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":false,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","email"],"type":"object"},"rentalObject":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"category":{"enum":["APARTMENT"],"type":"string"},"details":{"properties":{"amountOfResidents":{"type":"integer"},"moveInDate":{"type":"string"}},"type":"object"},"furnishingStandard":{"enum":["FURNISHED","UNFURNISHED"],"type":"string"},"id":{"type":"integer"},"keychain":{"properties":{"comment":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"keys":{"items":{"properties":{"name":{"type":"string"},"quantity":{"type":"integer"}},"required":["name","quantity"],"type":"object"},"type":"array"}},"type":"object"},"rooms":{"items":{"properties":{"comment":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"name":{"type":"string"}},"required":["name"],"type":"object"},"type":"array"},"utilities":{"properties":{"coldWater":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"electricity":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"gas":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"heating":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"hotWater":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"}},"type":"object"}},"required":["category","address"],"type":"object"},"tenant":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":false,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","email"],"type":"object"},"type":{"enum":["MOVE_IN","MOVE_OUT","INSPECTION"],"type":"string"}},"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}', {
			type,
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a list of possible tenant upsell opportunities for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/upsellOpportunities}
	 * @returns {Promise} A promise
	 */
	upsellOpportunities(type, session_id, responseType = 'json') {

		this.logger('Calling API function "upsellOpportunities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/opportunities', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Signing
 * @extends SmartmieteAPI
 */
export class SigningApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Signing"...');

	}


	/**
	 * Add a signature to the session
	 * @param {} session_id Required session id
	 * @param {} signer_id Required signer id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/addSignature}
	 * @returns {Promise} A promise
	 */
	addSignature(session_id, signer_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addSignature" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"anyOf":[{"additionalProperties":false,"properties":{},"type":"object"},{"properties":{"signatureFileToken":{"type":"string"}},"type":"object"},{"properties":{},"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/signing/{session_id}/{signer_id}', {
			session_id,
			signer_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new signing session.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.callback 
	 * @param {string} data.body.method  Enum: ['offline', 'token', 'verified_digital_signing']
	 * @param {string} data.body.representative 
	 * @param {array} data.body.signers 
	 * @param {string} data.body.token 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createSigning}
	 * @returns {Promise} A promise
	 */
	createSigning({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"callback":{"type":"string"},"method":{"enum":["offline","token","verified_digital_signing"],"type":"string"},"representative":{"type":"string"},"signers":{"items":{"items":{"additionalProperties":false,"properties":{"details":{"properties":{"errorRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a failed signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"},"successRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a successful signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"}},"type":"object"},"id":{"type":"string"},"signer":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":false,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","email"],"type":"object"},"status":{"enum":["waiting","pending","signed","declined"],"type":"string"},"type":{"enum":["landlord","tenant","co-tenant","bail","witness"],"type":"string"}},"required":["type","signer"],"type":"object"},"type":"array"},"type":"array"},"token":{"type":"string"}},"required":["method","token","signers"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signing';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Cancel and delete an ongoing signing session
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteSigning}
	 * @returns {Promise} A promise
	 */
	deleteSigning(session_id, responseType = 'json') {

		this.logger('Calling API function "deleteSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/signing/{session_id}', {
			session_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the state of the signer
	 * @param {} session_id Required session id
	 * @param {} signer_id Required signer id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSignerStatus}
	 * @returns {Promise} A promise
	 */
	getSignerStatus(session_id, signer_id, responseType = 'json') {

		this.logger('Calling API function "getSignerStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/signing/{session_id}/{signer_id}', {
			session_id,
			signer_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get status of a signing session.
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSigningStatus}
	 * @returns {Promise} A promise
	 */
	getSigningStatus(session_id, responseType = 'json') {

		this.logger('Calling API function "getSigningStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/signing/{session_id}', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the PDF file to be signed
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/previewDocument}
	 * @returns {Promise} A promise
	 */
	previewDocument(session_id, responseType = 'json') {

		this.logger('Calling API function "previewDocument" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/signing/{session_id}/preview', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Handles a Nect signing event callback. This endpoint expects a `Authorization` header providing a JWT token with a valid `nect_token` claim.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.documentId 
	 * @param {string} data.body.documentReference 
	 * @param {string} data.body.reason 
	 * @param {string} data.body.signatoryId 
	 * @param {string} data.body.signatoryReference 
	 * @param {boolean} data.body.success 
	 * @param {string} data.body.time 
	 * @param {string} data.body.type  Enum: ['Created', 'Accessed', 'Identifying', 'Identified', 'Opened', 'Signed', 'Finished', 'Deleted']

	 * @see {@link file:///./redoc-husleieapi.html#operation/signingCallbackNect}
	 * @returns {Promise} A promise
	 */
	signingCallbackNect({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "signingCallbackNect" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"documentId":{"type":"string"},"documentReference":{"type":"string"},"reason":{"type":"string"},"signatoryId":{"type":"string"},"signatoryReference":{"type":"string"},"success":{"type":"boolean"},"time":{"type":"string"},"type":{"enum":["Created","Accessed","Identifying","Identified","Opened","Signed","Finished","Deleted"],"type":"string"}},"required":["type","time","documentId","success","reason"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signing/callback/nect';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Signup
 * @extends SmartmieteAPI
 */
export class SignupApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Signup"...');

	}


	/**
	 * Check if given email is in use
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.email Unique email

	 * @see {@link file:///./redoc-husleieapi.html#operation/checkEmailInUse}
	 * @returns {Promise} A promise
	 */
	checkEmailInUse({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "checkEmailInUse" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"email":{"description":"Unique email","format":"email","maxLength":255,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signup/email/in/use';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Endpoint to create user through signup funnel
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.cellPhone Users cell phone. See the verified object, and seperate API-methods for performing the validation.
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email Unique email
	 * @param {string} data.body.familyName Family name
	 * @param {string} data.body.givenName Given name
	 * @param {string} data.body.language Language in ISO 639-1 format, for example nb-NO
	 * @param {string} data.body.password Password
	 * @param {string} data.body.role  Enum: ['tenant', 'landlord']
	 * @param {string} data.body.targetAction Target action after signup, only values consisting of a-z and A-Z are allowed
	 * @param {object} data.body.terms 
	 * @param {boolean} data.body.terms.GDPR 
	 * @param {boolean} data.body.terms.emailConsent 
	 * @param {boolean} data.body.terms.terms 
	 * @param {boolean} data.body.terms.termsAndPrivacyStatement 
	 * @param {string} data.body.timezone Time zone in TZ database name format, for example Europe/Oslo
	 * @param {object} data.body.title 
	 * @param {string} data.body.title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.title.professional From English honorifics, academic and professional, Dr, Professor etc  Enum: ['PROF', 'DR', 'PROF_DR']

	 * @see {@link file:///./redoc-husleieapi.html#operation/createUser}
	 * @returns {Promise} A promise
	 */
	createUser({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"cellPhone":{"description":"Users cell phone. See the verified object, and seperate API-methods for performing the validation.","maxLength":20,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"description":"Unique email","format":"email","maxLength":255,"type":"string"},"familyName":{"description":"Family name","maxLength":120,"type":"string"},"givenName":{"description":"Given name","maxLength":120,"type":"string"},"language":{"description":"Language in ISO 639-1 format, for example nb-NO","nullable":true,"type":"string"},"password":{"description":"Password","maxLength":255,"minLength":8,"type":"string"},"role":{"enum":["tenant","landlord"],"type":"string"},"targetAction":{"description":"Target action after signup, only values consisting of a-z and A-Z are allowed","pattern":"^[a-zA-Z]+$","type":"string"},"terms":{"properties":{"GDPR":{"nullable":true,"type":"boolean"},"emailConsent":{"nullable":true,"type":"boolean"},"terms":{"nullable":true,"type":"boolean"},"termsAndPrivacyStatement":{"nullable":true,"type":"boolean"}},"type":"object"},"timezone":{"description":"Time zone in TZ database name format, for example Europe/Oslo","nullable":true,"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"},"professional":{"description":"From English honorifics, academic and professional, Dr, Professor etc ","enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signup/user';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Tenancy
 * @extends SmartmieteAPI
 */
export class TenancyApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Tenancy"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.category  Enum: ['contract', 'depgar', 'takeover_protocol', 'inspection_report', 'contract_downloaded_for_offline_signing', 'rent_adjustment_letter', 'other'] Default: [other]
	 * @param {string} data.body.contentType 
	 * @param {string} data.body.created 
	 * @param {boolean} data.body.deletable 
	 * @param {string} data.body.fileName 
	 * @param {integer} data.body.id 
	 * @param {array} data.body.mediaLinks 
	 * @param {string} data.body.mediaLinks[].contentType 
	 * @param {string} data.body.mediaLinks[].type  Enum: ['original', 'thumbnail']
	 * @param {string} data.body.mediaLinks[].url 
	 * @param {integer} data.body.mediaLinks[].width 
	 * @param {integer} data.body.size 
	 * @param {string} data.body.token 

	 * @see {@link file:///./redoc-husleieapi.html#operation/addLandlordTenancyDocument}
	 * @returns {Promise} A promise
	 */
	addLandlordTenancyDocument(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addLandlordTenancyDocument" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"category":{"default":"other","enum":["contract","depgar","takeover_protocol","inspection_report","contract_downloaded_for_offline_signing","rent_adjustment_letter","other"],"type":"string"},"contentType":{"readOnly":true,"type":"string"},"created":{"format":"date-time","readOnly":true,"type":"string"},"deletable":{"type":"boolean"},"fileName":{"readOnly":true,"type":"string"},"id":{"readOnly":true,"type":"integer"},"mediaLinks":{"items":{"properties":{"contentType":{"type":"string"},"type":{"enum":["original","thumbnail"],"type":"string"},"url":{"type":"string"},"width":{"type":"integer"}},"required":["type","contentType","url"],"type":"object"},"readOnly":true,"title":"LettingMediaLinks","type":"array"},"size":{"readOnly":true,"type":"integer"},"token":{"type":"string"}},"title":"TenancyDocument","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/documents', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/addLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	addLandlordTenancySubProduct(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"allOf":[{"properties":{"category":{"default":2,"type":"integer"},"effectiveDate":{"format":"date","nullable":true,"type":"string"},"invoiceInterval":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"name":{"type":"string"},"price":{"type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancySubProductBarebone","type":"object"}],"properties":{"id":{"type":"integer"}},"title":"TenancySubProduct","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.address 
	 * @param {string} data.body.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.address.postalArea Postal area
	 * @param {string} data.body.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.address.street Street name and number
	 * @param {string} data.body.address.street2 Secondary street name and number
	 * @param {string} data.body.cellPhone 
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email 
	 * @param {string} data.body.familyName 
	 * @param {string} data.body.fullName 
	 * @param {string} data.body.givenName 
	 * @param {string} data.body.id 
	 * @param {object} data.body.title 
	 * @param {string} data.body.title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/addLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	addLandlordTenancyTenant(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"familyName":{"type":"string"},"fullName":{"nullable":true,"type":"string"},"givenName":{"type":"string"},"id":{"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"}},"type":"object"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.amountOfResidents 
	 * @param {array} data.body.coTenants 
	 * @param {object} data.body.coTenants[].address 
	 * @param {string} data.body.coTenants[].address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.coTenants[].address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.coTenants[].address.postalArea Postal area
	 * @param {string} data.body.coTenants[].address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.coTenants[].address.postalZipcode Postal Zipcode
	 * @param {string} data.body.coTenants[].address.street Street name and number
	 * @param {string} data.body.coTenants[].address.street2 Secondary street name and number
	 * @param {string} data.body.coTenants[].cellPhone 
	 * @param {string} data.body.coTenants[].dateOfBirth 
	 * @param {string} data.body.coTenants[].email 
	 * @param {string} data.body.coTenants[].familyName 
	 * @param {string} data.body.coTenants[].fullName 
	 * @param {string} data.body.coTenants[].givenName 
	 * @param {string} data.body.coTenants[].id 
	 * @param {object} data.body.coTenants[].title 
	 * @param {string} data.body.coTenants[].title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.coTenants[].type  Enum: ['tenant', 'cotenant'] Default: [tenant]
	 * @param {object} data.body.deposits 
	 * @param {boolean} data.body.deposits.depositAccountLandlord  Default: [0]
	 * @param {array} data.body.deposits.depositSolutions 
	 * @param {boolean} data.body.deposits.depositSolutions[].isPermitted 
	 * @param {string} data.body.deposits.depositSolutions[].label 
	 * @param {string} data.body.deposits.depositSolutions[].name 
	 * @param {number} data.body.deposits.months 
	 * @param {boolean} data.body.deposits.ownDefined  Default: [0]
	 * @param {number} data.body.deposits.ownDefinedAmount 
	 * @param {object} data.body.details 
	 * @param {integer} data.body.details.bindingPeriod 
	 * @param {string} data.body.details.customClientId 
	 * @param {integer} data.body.details.noticePeriod 
	 * @param {string} data.body.details.poNumber 
	 * @param {string} data.body.endDate 
	 * @param {array} data.body.fileTokens 
	 * @param {object} data.body.rent 
	 * @param {number} data.body.rent.billableRent Total billable rent including sub-products
	 * @param {integer} data.body.rent.bindingPeriod 
	 * @param {number} data.body.rent.dueDay 
	 * @param {string} data.body.rent.duePeriod  Enum: ['advance', 'current', 'postpay']
	 * @param {string} data.body.rent.invoicePeriod  Enum: ['month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semi-anual'] Default: [month]
	 * @param {string} data.body.rent.limitedRentDurationType According to the law, only the following three reasons for a time limit are permissible. Enum: ['OWN_USE', 'RENOVATION', 'LOCATION_REQUIRED']
	 * @param {string} data.body.rent.limitedRentReason Text reason for time limits
	 * @param {number} data.body.rent.rent Tenancy rent
	 * @param {integer} data.body.rent.vat 
	 * @param {object} data.body.rentAdjustment 
	 * @param {boolean} data.body.rentAdjustment.firstUsedAndRented  Default: [0]
	 * @param {boolean} data.body.rentAdjustment.occupancyAfterRenovations  Default: [0]
	 * @param {object} data.body.rentAdjustment.previousTenant 
	 * @param {boolean} data.body.rentAdjustment.previousTenant.available  Default: [0]
	 * @param {string} data.body.rentAdjustment.previousTenant.lastPaymentDate 
	 * @param {number} data.body.rentAdjustment.previousTenant.lastRent 
	 * @param {boolean} data.body.rentAdjustment.renovationsLastThreeYears  Default: [0]
	 * @param {boolean} data.body.rentAdjustment.rentRegulation  Default: [0]
	 * @param {string} data.body.rentAdjustment.rentalAdjustment  Enum: ['COMPARABLE_RENT', 'GRADUATED_RENT', 'INDEX_RENT'] Default: [COMPARABLE_RENT]
	 * @param {array} data.body.rentAdjustment.tiers 
	 * @param {string} data.body.rentAdjustment.tiers[].date 
	 * @param {number} data.body.rentAdjustment.tiers[].rent 
	 * @param {object} data.body.rentalCalculation 
	 * @param {integer} data.body.rentalObjectId 
	 * @param {string} data.body.startDate 
	 * @param {object} data.body.subscription 
	 * @param {object} data.body.subscription.payment 
	 * @param {object} data.body.subscription.payment.stripe 
	 * @param {string} data.body.subscription.payment.stripe.paymentMethodId 
	 * @param {number} data.body.subscription.price 
	 * @param {integer} data.body.subscription.subscriptionPlanId 
	 * @param {string} data.body.subscription.subscriptionPlanName 
	 * @param {object} data.body.tenant 
	 * @param {object} data.body.tenant.address 
	 * @param {string} data.body.tenant.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.tenant.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.tenant.address.postalArea Postal area
	 * @param {string} data.body.tenant.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.tenant.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.tenant.address.street Street name and number
	 * @param {string} data.body.tenant.address.street2 Secondary street name and number
	 * @param {string} data.body.tenant.cellPhone 
	 * @param {string} data.body.tenant.dateOfBirth 
	 * @param {string} data.body.tenant.email 
	 * @param {string} data.body.tenant.familyName 
	 * @param {string} data.body.tenant.fullName 
	 * @param {string} data.body.tenant.givenName 
	 * @param {string} data.body.tenant.id 
	 * @param {object} data.body.tenant.title 
	 * @param {string} data.body.tenant.title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.tenant.type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/createLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	createLandlordTenancy(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"amountOfResidents":{"type":"integer"},"coTenants":{"items":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"familyName":{"type":"string"},"fullName":{"nullable":true,"type":"string"},"givenName":{"type":"string"},"id":{"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"}},"type":"object"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"},"type":"array"},"deposits":{"properties":{"depositAccountLandlord":{"default":false,"type":"boolean"},"depositSolutions":{"items":{"properties":{"isPermitted":{"type":"boolean"},"label":{"nullable":true,"type":"string"},"name":{"type":"string"}},"type":"object"},"type":"array"},"months":{"type":"number"},"ownDefined":{"default":false,"type":"boolean"},"ownDefinedAmount":{"type":"number"}},"title":"TenancyDeposit","type":"object"},"details":{"properties":{"bindingPeriod":{"nullable":true,"type":"integer"},"customClientId":{"nullable":true,"type":"string"},"noticePeriod":{"nullable":true,"type":"integer"},"poNumber":{"nullable":true,"type":"string"}},"type":"object"},"endDate":{"format":"date","nullable":true,"type":"string"},"fileTokens":{"items":{"type":"string"},"type":"array"},"rent":{"properties":{"billableRent":{"description":"Total billable rent including sub-products","type":"number"},"bindingPeriod":{"nullable":true,"type":"integer"},"dueDay":{"type":"number"},"duePeriod":{"enum":["advance","current","postpay"],"type":"string"},"invoicePeriod":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"limitedRentDurationType":{"description":"According to the law, only the following three reasons for a time limit are permissible.","enum":["OWN_USE","RENOVATION","LOCATION_REQUIRED"],"nullable":true,"type":"string"},"limitedRentReason":{"description":"Text reason for time limits","nullable":true,"type":"string"},"rent":{"description":"Tenancy rent","type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancyRent","type":"object"},"rentAdjustment":{"nullable":true,"properties":{"firstUsedAndRented":{"default":false,"type":"boolean"},"occupancyAfterRenovations":{"default":false,"type":"boolean"},"previousTenant":{"properties":{"available":{"default":false,"type":"boolean"},"lastPaymentDate":{"format":"date","type":"string"},"lastRent":{"type":"number"}},"type":"object"},"renovationsLastThreeYears":{"default":false,"type":"boolean"},"rentRegulation":{"default":false,"type":"boolean"},"rentalAdjustment":{"default":"COMPARABLE_RENT","enum":["COMPARABLE_RENT","GRADUATED_RENT","INDEX_RENT"],"type":"string"},"tiers":{"items":{"properties":{"date":{"format":"date","type":"string"},"rent":{"type":"number"}},"type":"object"},"nullable":true,"type":"array"}},"title":"TenancyRentAdjustment","type":"object"},"rentalCalculation":{"type":"object"},"rentalObjectId":{"type":"integer"},"startDate":{"format":"date","type":"string"},"subscription":{"properties":{"payment":{"properties":{"stripe":{"properties":{"paymentMethodId":{"type":"string"}},"type":"object"}},"type":"object"},"price":{"readOnly":true,"type":"number"},"subscriptionPlanId":{"type":"integer"},"subscriptionPlanName":{"readOnly":true,"type":"string"}},"title":"TenancySubscription","type":"object"},"tenant":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"familyName":{"type":"string"},"fullName":{"nullable":true,"type":"string"},"givenName":{"type":"string"},"id":{"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"}},"type":"object"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"}},"title":"Create Tenancy","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.effectiveDate 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.meta 
	 * @param {number} data.body.newRent 
	 * @param {string} data.body.notificationDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	createTenancyRentAdjustment(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"effectiveDate":{"format":"date","type":"string"},"isMietCheck":{"type":"boolean"},"meta":{"type":"object"},"newRent":{"format":"float","minimum":1,"type":"number"},"notificationDate":{"format":"date","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancy(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} document_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyDocument}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyDocument(landlord_id, tenancy_id, document_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyDocument" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/documents/{document_id}', {
			landlord_id,
			tenancy_id,
			document_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} subproduct_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancySubProduct(landlord_id, tenancy_id, subproduct_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts/{subproduct_id}', {
			landlord_id,
			tenancy_id,
			subproduct_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} tenant_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyTenant(landlord_id, tenancy_id, tenant_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants/{tenant_id}', {
			landlord_id,
			tenancy_id,
			tenant_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} adjustment_id Required Rent Adjustment ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	deleteTenancyRentAdjustment(landlord_id, tenancy_id, adjustment_id, responseType = 'json') {

		this.logger('Calling API function "deleteTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/{adjustment_id}', {
			landlord_id,
			tenancy_id,
			adjustment_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.adjustment 
	 * @param {string} data.body.adjustment.attribute 
	 * @param {number} data.body.adjustment.cappingLimit 
	 * @param {number} data.body.adjustment.currentRent 
	 * @param {string} data.body.adjustment.description 
	 * @param {string} data.body.adjustment.effectiveDate 
	 * @param {string} data.body.adjustment.location 
	 * @param {number} data.body.adjustment.newRent 
	 * @param {string} data.body.adjustment.standard 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.landlord 
	 * @param {string} data.body.landlord.fullName 
	 * @param {string} data.body.landlord.postCodeAndCity 
	 * @param {string} data.body.landlord.street 
	 * @param {object} data.body.rentalObject 
	 * @param {integer} data.body.rentalObject.dateOfConstruction 
	 * @param {string} data.body.rentalObject.postCodeAndCity 
	 * @param {number} data.body.rentalObject.squareMeters 
	 * @param {string} data.body.rentalObject.street 
	 * @param {integer} data.body.tenancyRentChangeId 
	 * @param {object} data.body.tenant 
	 * @param {string} data.body.tenant.fullName 

	 * @see {@link file:///./redoc-husleieapi.html#operation/generateRentAdjustmentPDF}
	 * @returns {Promise} A promise
	 */
	generateRentAdjustmentPDF(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "generateRentAdjustmentPDF" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"example":{"adjustment":{"attribute":"Some attribute","cappingLimit":1500,"currentRent":1000,"description":"Some description","effectiveDate":"2023-11-10","location":"A","newRent":1200,"standard":"5"},"landlord":{"fullName":"landlord name","postCodeAndCity":"landlord postcode And City","street":"landlord street 1 and 2"},"rentalObject":{"dateOfConstruction":"1905","postCodeAndCity":"rentalObject postcode And City","squareMeters":100,"street":"rentalObject street 1 and 2"},"tenant":{"fullName":"tenant name"}},"properties":{"adjustment":{"properties":{"attribute":{"type":"string"},"cappingLimit":{"format":"float","type":"number"},"currentRent":{"format":"float","type":"number"},"description":{"type":"string"},"effectiveDate":{"format":"date","type":"string"},"location":{"type":"string"},"newRent":{"format":"float","type":"number"},"standard":{"type":"string"}},"type":"object"},"isMietCheck":{"type":"boolean"},"landlord":{"properties":{"fullName":{"type":"string"},"postCodeAndCity":{"type":"string"},"street":{"type":"string"}},"type":"object"},"rentalObject":{"properties":{"dateOfConstruction":{"type":"integer"},"postCodeAndCity":{"type":"string"},"squareMeters":{"type":"number"},"street":{"type":"string"}},"type":"object"},"tenancyRentChangeId":{"type":"integer"},"tenant":{"properties":{"fullName":{"type":"string"}},"type":"object"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/rent/adjustments/generate_pdf', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getAllTenancyRentAdjustments}
	 * @returns {Promise} A promise
	 */
	getAllTenancyRentAdjustments(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getAllTenancyRentAdjustments" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancies}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancies(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancies" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancy(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyDocuments}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyDocuments(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyDocuments" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/documents', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyRent}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyRent(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyRent" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/rent', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} subproduct_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancySubProduct(landlord_id, tenancy_id, subproduct_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts/{subproduct_id}', {
			landlord_id,
			tenancy_id,
			subproduct_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancySubProducts}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancySubProducts(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancySubProducts" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} tenant_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyTenant(landlord_id, tenancy_id, tenant_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants/{tenant_id}', {
			landlord_id,
			tenancy_id,
			tenant_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyTenants}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyTenants(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyTenants" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} adjustment_id Required Rent Adjustment ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	getTenancyRentAdjustment(landlord_id, tenancy_id, adjustment_id, responseType = 'json') {

		this.logger('Calling API function "getTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/{adjustment_id}', {
			landlord_id,
			tenancy_id,
			adjustment_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTenancyRentAdjustmentsStatus}
	 * @returns {Promise} A promise
	 */
	getTenancyRentAdjustmentsStatus(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getTenancyRentAdjustmentsStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/status', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTenancySubscriptionPlans}
	 * @returns {Promise} A promise
	 */
	getTenancySubscriptionPlans(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getTenancySubscriptionPlans" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/available_subscriptions', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} adjustment_id Required Rent Adjustment ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.effectiveDate 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.meta 
	 * @param {number} data.body.newRent 
	 * @param {string} data.body.notificationDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	patchTenancyRentAdjustment(landlord_id, tenancy_id, adjustment_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"effectiveDate":{"format":"date","type":"string"},"isMietCheck":{"type":"boolean"},"meta":{"type":"object"},"newRent":{"format":"float","minimum":1,"type":"number"},"notificationDate":{"format":"date","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/{adjustment_id}', {
			landlord_id,
			tenancy_id,
			adjustment_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.effectiveDate 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.meta 
	 * @param {number} data.body.newRent 
	 * @param {string} data.body.notificationDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/previewTenancyRentAdjustmentLetter}
	 * @returns {Promise} A promise
	 */
	previewTenancyRentAdjustmentLetter(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "previewTenancyRentAdjustmentLetter" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"effectiveDate":{"format":"date","type":"string"},"isMietCheck":{"type":"boolean"},"meta":{"type":"object"},"newRent":{"format":"float","minimum":1,"type":"number"},"notificationDate":{"format":"date","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/preview/letter', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.amountOfResidents 
	 * @param {object} data.body.deposits 
	 * @param {boolean} data.body.deposits.depositAccountLandlord  Default: [0]
	 * @param {array} data.body.deposits.depositSolutions 
	 * @param {boolean} data.body.deposits.depositSolutions[].isPermitted 
	 * @param {string} data.body.deposits.depositSolutions[].label 
	 * @param {string} data.body.deposits.depositSolutions[].name 
	 * @param {number} data.body.deposits.months 
	 * @param {boolean} data.body.deposits.ownDefined  Default: [0]
	 * @param {number} data.body.deposits.ownDefinedAmount 
	 * @param {string} data.body.endDate 
	 * @param {object} data.body.rentAdjustment 
	 * @param {boolean} data.body.rentAdjustment.firstUsedAndRented  Default: [0]
	 * @param {boolean} data.body.rentAdjustment.occupancyAfterRenovations  Default: [0]
	 * @param {object} data.body.rentAdjustment.previousTenant 
	 * @param {boolean} data.body.rentAdjustment.previousTenant.available  Default: [0]
	 * @param {string} data.body.rentAdjustment.previousTenant.lastPaymentDate 
	 * @param {number} data.body.rentAdjustment.previousTenant.lastRent 
	 * @param {boolean} data.body.rentAdjustment.renovationsLastThreeYears  Default: [0]
	 * @param {boolean} data.body.rentAdjustment.rentRegulation  Default: [0]
	 * @param {string} data.body.rentAdjustment.rentalAdjustment  Enum: ['COMPARABLE_RENT', 'GRADUATED_RENT', 'INDEX_RENT'] Default: [COMPARABLE_RENT]
	 * @param {array} data.body.rentAdjustment.tiers 
	 * @param {string} data.body.rentAdjustment.tiers[].date 
	 * @param {number} data.body.rentAdjustment.tiers[].rent 
	 * @param {string} data.body.startDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancy(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"amountOfResidents":{"type":"integer"},"deposits":{"properties":{"depositAccountLandlord":{"default":false,"type":"boolean"},"depositSolutions":{"items":{"properties":{"isPermitted":{"type":"boolean"},"label":{"nullable":true,"type":"string"},"name":{"type":"string"}},"type":"object"},"type":"array"},"months":{"type":"number"},"ownDefined":{"default":false,"type":"boolean"},"ownDefinedAmount":{"type":"number"}},"title":"TenancyDeposit","type":"object"},"endDate":{"format":"date","nullable":true,"type":"string"},"rentAdjustment":{"nullable":true,"properties":{"firstUsedAndRented":{"default":false,"type":"boolean"},"occupancyAfterRenovations":{"default":false,"type":"boolean"},"previousTenant":{"properties":{"available":{"default":false,"type":"boolean"},"lastPaymentDate":{"format":"date","type":"string"},"lastRent":{"type":"number"}},"type":"object"},"renovationsLastThreeYears":{"default":false,"type":"boolean"},"rentRegulation":{"default":false,"type":"boolean"},"rentalAdjustment":{"default":"COMPARABLE_RENT","enum":["COMPARABLE_RENT","GRADUATED_RENT","INDEX_RENT"],"type":"string"},"tiers":{"items":{"properties":{"date":{"format":"date","type":"string"},"rent":{"type":"number"}},"type":"object"},"nullable":true,"type":"array"}},"title":"TenancyRentAdjustment","type":"object"},"startDate":{"format":"date","type":"string"}},"title":"Update Tenancy Germany","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.billableRent Total billable rent including sub-products
	 * @param {integer} data.body.bindingPeriod 
	 * @param {number} data.body.dueDay 
	 * @param {string} data.body.duePeriod  Enum: ['advance', 'current', 'postpay']
	 * @param {string} data.body.invoicePeriod  Enum: ['month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semi-anual'] Default: [month]
	 * @param {string} data.body.limitedRentDurationType According to the law, only the following three reasons for a time limit are permissible. Enum: ['OWN_USE', 'RENOVATION', 'LOCATION_REQUIRED']
	 * @param {string} data.body.limitedRentReason Text reason for time limits
	 * @param {number} data.body.rent Tenancy rent
	 * @param {integer} data.body.vat 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancyRent}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancyRent(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancyRent" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"billableRent":{"description":"Total billable rent including sub-products","type":"number"},"bindingPeriod":{"nullable":true,"type":"integer"},"dueDay":{"type":"number"},"duePeriod":{"enum":["advance","current","postpay"],"type":"string"},"invoicePeriod":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"limitedRentDurationType":{"description":"According to the law, only the following three reasons for a time limit are permissible.","enum":["OWN_USE","RENOVATION","LOCATION_REQUIRED"],"nullable":true,"type":"string"},"limitedRentReason":{"description":"Text reason for time limits","nullable":true,"type":"string"},"rent":{"description":"Tenancy rent","type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancyRent","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/rent', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} subproduct_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancySubProduct(landlord_id, tenancy_id, subproduct_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"allOf":[{"properties":{"category":{"default":2,"type":"integer"},"effectiveDate":{"format":"date","nullable":true,"type":"string"},"invoiceInterval":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"name":{"type":"string"},"price":{"type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancySubProductBarebone","type":"object"}],"properties":{"id":{"type":"integer"}},"title":"TenancySubProduct","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts/{subproduct_id}', {
			landlord_id,
			tenancy_id,
			subproduct_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} tenant_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.address 
	 * @param {string} data.body.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [DE]
	 * @param {string} data.body.address.postalArea Postal area
	 * @param {string} data.body.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.address.street Street name and number
	 * @param {string} data.body.address.street2 Secondary street name and number
	 * @param {string} data.body.cellPhone 
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email 
	 * @param {string} data.body.familyName 
	 * @param {string} data.body.fullName 
	 * @param {string} data.body.givenName 
	 * @param {string} data.body.id 
	 * @param {object} data.body.title 
	 * @param {string} data.body.title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancyTenant(landlord_id, tenancy_id, tenant_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"DE","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"},"street2":{"description":"Secondary street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"familyName":{"type":"string"},"fullName":{"nullable":true,"type":"string"},"givenName":{"type":"string"},"id":{"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"}},"type":"object"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants/{tenant_id}', {
			landlord_id,
			tenancy_id,
			tenant_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.origin 
	 * @param {object} data.body.payment 
	 * @param {object} data.body.payment.stripe 
	 * @param {string} data.body.payment.stripe.paymentMethodId 
	 * @param {string} data.body.payment.type  Enum: ['stripe']
	 * @param {integer} data.body.subscriptionPlanId 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateTenancySubscription}
	 * @returns {Promise} A promise
	 */
	updateTenancySubscription(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateTenancySubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"origin":{"pattern":"^[a-z0-9_]+$","type":"string"},"payment":{"properties":{"stripe":{"properties":{"paymentMethodId":{"type":"string"}},"type":"object"},"type":{"enum":["stripe"],"type":"string"}},"required":["type","stripe"],"type":"object"},"subscriptionPlanId":{"type":"integer"}},"required":["subscriptionPlanId"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/subscription', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * TenancyAccounting
 * @extends SmartmieteAPI
 */
export class TenancyAccountingApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "TenancyAccounting"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.heatingCost Heating cost
	 * @param {number} data.body.receivedAmount Received payment amount
	 * @param {string} data.body.receivedDate Received payment date
	 * @param {number} data.body.rentAmount Cold rent
	 * @param {integer} data.body.rentalPeriodMonth Rental period month
	 * @param {integer} data.body.rentalPeriodYear Rental period year
	 * @param {number} data.body.utilityCost Utility cost

	 * @param data.query {object}
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/createLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	createLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, { body, query } = {}, responseType = 'json') {

		this.logger('Calling API function "createLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"heatingCost":{"description":"Heating cost","format":"float","type":"number"},"receivedAmount":{"description":"Received payment amount","format":"float","type":"number"},"receivedDate":{"description":"Received payment date","format":"date","type":"string"},"rentAmount":{"description":"Cold rent","format":"float","type":"number"},"rentalPeriodMonth":{"description":"Rental period month","type":"integer"},"rentalPeriodYear":{"description":"Rental period year","type":"integer"},"utilityCost":{"description":"Utility cost","format":"float","type":"number"}},"required":["rentAmount","rentalPeriodYear","rentalPeriodMonth","heatingCost","utilityCost"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} bundle_uuid Required bundle UUID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, bundle_uuid, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker/{bundle_uuid}', {
			landlord_id,
			tenancy_id,
			bundle_uuid,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} bundle_uuid Required bundle UUID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.heatingCost Heating cost
	 * @param {number} data.body.rentAmount Cold rent
	 * @param {number} data.body.utilityCost Utility cost

	 * @see {@link file:///./redoc-husleieapi.html#operation/editLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	editLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, bundle_uuid, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "editLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"heatingCost":{"description":"Heating cost","format":"float","type":"number"},"rentAmount":{"description":"Cold rent","format":"float","type":"number"},"utilityCost":{"description":"Utility cost","format":"float","type":"number"}},"required":["rentAmount","heatingCost","utilityCost"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker/{bundle_uuid}', {
			landlord_id,
			tenancy_id,
			bundle_uuid,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} bundle_uuid Required bundle UUID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.receivedAmount Rent amount received
	 * @param {string} data.body.receivedDate Rent payment date

	 * @see {@link file:///./redoc-husleieapi.html#operation/editPaymentLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	editPaymentLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, bundle_uuid, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "editPaymentLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"receivedAmount":{"description":"Rent amount received","format":"float","type":"number"},"receivedDate":{"description":"Rent payment date","format":"date","type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker/{bundle_uuid}/payment', {
			landlord_id,
			tenancy_id,
			bundle_uuid,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} bundle_uuid Required bundle UUID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.receivedDate Rent payment date

	 * @see {@link file:///./redoc-husleieapi.html#operation/fullPaymentLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	fullPaymentLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, bundle_uuid, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "fullPaymentLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"receivedDate":{"description":"Rent payment date","format":"date","type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker/{bundle_uuid}/full_payment', {
			landlord_id,
			tenancy_id,
			bundle_uuid,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyAccountingSummary}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyAccountingSummary(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyAccountingSummary" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/summary', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/getManyLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	getManyLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getManyLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} bundle_uuid Required bundle UUID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getOneLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	getOneLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, bundle_uuid, responseType = 'json') {

		this.logger('Calling API function "getOneLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker/{bundle_uuid}', {
			landlord_id,
			tenancy_id,
			bundle_uuid,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/resetLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	resetLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "resetLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} bundle_uuid Required bundle UUID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.sternness How stern the email should be (higher = tougher) Enum: ['1', '2']

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendReminderLandlordTenancyAccountingRentTracker}
	 * @returns {Promise} A promise
	 */
	sendReminderLandlordTenancyAccountingRentTracker(landlord_id, tenancy_id, bundle_uuid, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendReminderLandlordTenancyAccountingRentTracker" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"sternness":{"description":"How stern the email should be (higher = tougher)","enum":["1","2"],"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/rent_tracker/{bundle_uuid}/send_reminder', {
			landlord_id,
			tenancy_id,
			bundle_uuid,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Token
 * @extends SmartmieteAPI
 */
export class TokenApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Token"...');

	}


	/**
	 * Authenticate with HTTP BasicAuth, and a JWT token will be returned. The JWT token is required to access the remaining API endpoints.
	 * @see {@link file:///./redoc-husleieapi.html#operation/getAuthenticationToken}
	 * @returns {Promise} A promise
	 */
	getAuthenticationToken(responseType = 'json') {

		this.logger('Calling API function "getAuthenticationToken" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/token';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * This endpoint triggers a email with a timed login link to the user
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.email 

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendLoginEmailToken}
	 * @returns {Promise} A promise
	 */
	sendLoginEmailToken({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendLoginEmailToken" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"email":{"format":"email","type":"string"}},"required":["email"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/auth/email_token';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Tool
 * @extends SmartmieteAPI
 */
export class ToolApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Tool"...');

	}


	/**
	 *
	 * @param {} postal_code
	 * @see {@link file:///./redoc-husleieapi.html#operation/lookupRentIndex}
	 * @returns {Promise} A promise
	 */
	lookupRentIndex(postal_code, responseType = 'json') {

		this.logger('Calling API function "lookupRentIndex" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/tool/rent_index/{postal_code}', {
			postal_code,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Translation
 * @extends SmartmieteAPI
 */
export class TranslationApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Translation"...');

	}


	/**
	 * If successfull, this will return JSON object containing translations
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.projects {}Required Project or Projects
	 * @param data.query.languageCode {}Which country should we try to fetch translation for
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTranslations}
	 * @returns {Promise} A promise
	 */
	getTranslations({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "getTranslations" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/translations';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Upsell
 * @extends SmartmieteAPI
 */
export class UpsellApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Upsell"...');

	}


	/**
	 * Create an upsell session
	 * @param {} upsell_type Required upsell type
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createUpsell}
	 * @returns {Promise} A promise
	 */
	createUpsell(upsell_type, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"oneOf":[{"properties":{"plan":{"properties":{"id":{"type":"integer"}},"type":"object"},"token":{"type":"string"}},"required":["token"],"type":"object"}]}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/upsell/{upsell_type}', {
			upsell_type,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get upsell status
	 * @param {} upsell_type Required upsell type
	 * @param {} upsell_id Required upsell id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getUpsellStatus}
	 * @returns {Promise} A promise
	 */
	getUpsellStatus(upsell_type, upsell_id, responseType = 'json') {

		this.logger('Calling API function "getUpsellStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/upsell/{upsell_type}/{upsell_id}', {
			upsell_type,
			upsell_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * User
 * @extends SmartmieteAPI
 */
export class UserApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "User"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.status {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getUserContracts}
	 * @returns {Promise} A promise
	 */
	getUserContracts({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "getUserContracts" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/user/contracts';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Return all sessions of the requested type of the user
	 * @param {} type Required session type
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.status {}
	 * @param data.query.orderBy {}
	 * @param data.query.page {}
	 * @param data.query.pageSize {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getUserSessions}
	 * @returns {Promise} A promise
	 */
	getUserSessions(type, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getUserSessions" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/user/sessions/{type}', {
			type,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * UtilityCost
 * @extends SmartmieteAPI
 */
export class UtilityCostApi extends SmartmieteAPI {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "UtilityCost"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} utility_cost_calculation_id Required Utility Cost Calculation ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteOneLandlordUtilityCostCalculation}
	 * @returns {Promise} A promise
	 */
	deleteOneLandlordUtilityCostCalculation(landlord_id, utility_cost_calculation_id, responseType = 'json') {

		this.logger('Calling API function "deleteOneLandlordUtilityCostCalculation" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/calculations/{utility_cost_calculation_id}', {
			landlord_id,
			utility_cost_calculation_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} utility_cost_calculation_id Required Utility Cost Calculation ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/downloadOneLandlordUtilityCostCalculation}
	 * @returns {Promise} A promise
	 */
	downloadOneLandlordUtilityCostCalculation(landlord_id, utility_cost_calculation_id, responseType = 'json') {

		this.logger('Calling API function "downloadOneLandlordUtilityCostCalculation" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/calculations/{utility_cost_calculation_id}/download', {
			landlord_id,
			utility_cost_calculation_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.billingPeriodEnd Utility cost billing period end date
	 * @param {string} data.body.billingPeriodStart Utility cost billing period start date
	 * @param {string} data.body.landlordBankAccountNumber Landlord bank account number
	 * @param {string} data.body.settlementDueDate Utility cost settlement due date
	 * @param {string} data.body.statementDate Utility cost settlement date
	 * @param {array} data.body.utilityCosts 
	 * @param {object} data.body.utilityCosts[].allocation 
	 * @param {number} data.body.utilityCosts[].allocation.part Utility cost allocation part
	 * @param {number} data.body.utilityCosts[].allocation.partShare Utility cost allocation part share
	 * @param {number} data.body.utilityCosts[].allocation.partTotal Utility cost allocation part total
	 * @param {number} data.body.utilityCosts[].allocationKeyId Allocation key ID
	 * @param {boolean} data.body.utilityCosts[].apportionable Utility cost apportionable
	 * @param {number} data.body.utilityCosts[].buildingId Building ID
	 * @param {number} data.body.utilityCosts[].categoryId Utility cost category ID
	 * @param {object} data.body.utilityCosts[].cost 
	 * @param {string} data.body.utilityCosts[].cost.currency Utility cost currency
	 * @param {number} data.body.utilityCosts[].cost.share Utility cost share
	 * @param {number} data.body.utilityCosts[].cost.total Utility cost total
	 * @param {string} data.body.utilityCosts[].description Utility cost description
	 * @param {number} data.body.utilityCosts[].id Utility Cost ID
	 * @param {object} data.body.utilityCosts[].period 
	 * @param {number} data.body.utilityCosts[].period.days Utility cost days
	 * @param {string} data.body.utilityCosts[].period.from Utility cost period start date
	 * @param {string} data.body.utilityCosts[].period.to Utility cost period end date
	 * @param {number} data.body.utilityCosts[].rentalObjectId Rental object ID
	 * @param {string} data.body.utilityCosts[].type Utility cost type Enum: ['building', 'rental_object']
	 * @param {boolean} data.body.utilityCosts[].useInCalculation Use utility cost in calculation?

	 * @see {@link file:///./redoc-husleieapi.html#operation/generateOneLandlordUtilityCostCalculation}
	 * @returns {Promise} A promise
	 */
	generateOneLandlordUtilityCostCalculation(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "generateOneLandlordUtilityCostCalculation" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"billingPeriodEnd":{"description":"Utility cost billing period end date","format":"date","type":"string"},"billingPeriodStart":{"description":"Utility cost billing period start date","format":"date","type":"string"},"landlordBankAccountNumber":{"description":"Landlord bank account number","type":"string"},"settlementDueDate":{"description":"Utility cost settlement due date","format":"date","type":"string"},"statementDate":{"description":"Utility cost settlement date","format":"date","type":"string"},"utilityCosts":{"items":{"properties":{"allocation":{"properties":{"part":{"description":"Utility cost allocation part","nullable":true,"type":"number"},"partShare":{"description":"Utility cost allocation part share","format":"float","nullable":true,"type":"number"},"partTotal":{"description":"Utility cost allocation part total","format":"float","nullable":true,"type":"number"}},"type":"object"},"allocationKeyId":{"description":"Allocation key ID","nullable":true,"type":"number"},"apportionable":{"description":"Utility cost apportionable","type":"boolean"},"buildingId":{"description":"Building ID","nullable":true,"type":"number"},"categoryId":{"description":"Utility cost category ID","type":"number"},"cost":{"properties":{"currency":{"description":"Utility cost currency","type":"string"},"share":{"description":"Utility cost share","format":"float","type":"number"},"total":{"description":"Utility cost total","format":"float","type":"number"}},"required":["share","total","currency"],"type":"object"},"description":{"description":"Utility cost description","type":"string"},"id":{"description":"Utility Cost ID","type":"number"},"period":{"properties":{"days":{"description":"Utility cost days","type":"number"},"from":{"description":"Utility cost period start date","format":"date","type":"string"},"to":{"description":"Utility cost period end date","format":"date","type":"string"}},"required":["days","from","to"],"type":"object"},"rentalObjectId":{"description":"Rental object ID","nullable":true,"type":"number"},"type":{"description":"Utility cost type","enum":["building","rental_object"],"type":"string"},"useInCalculation":{"description":"Use utility cost in calculation?","type":"boolean"}},"type":"object"},"type":"array"}},"required":["billingPeriodStart","billingPeriodEnd"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/tenancies/{tenancy_id}/calculate', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.billingPeriodEnd Utility cost billing period end date
	 * @param {string} data.body.billingPeriodStart Utility cost billing period start date
	 * @param {string} data.body.landlordBankAccountNumber Landlord bank account number
	 * @param {string} data.body.settlementDueDate Utility cost settlement due date
	 * @param {string} data.body.statementDate Utility cost settlement date
	 * @param {array} data.body.utilityCosts 
	 * @param {object} data.body.utilityCosts[].allocation 
	 * @param {number} data.body.utilityCosts[].allocation.part Utility cost allocation part
	 * @param {number} data.body.utilityCosts[].allocation.partShare Utility cost allocation part share
	 * @param {number} data.body.utilityCosts[].allocation.partTotal Utility cost allocation part total
	 * @param {number} data.body.utilityCosts[].allocationKeyId Allocation key ID
	 * @param {boolean} data.body.utilityCosts[].apportionable Utility cost apportionable
	 * @param {number} data.body.utilityCosts[].buildingId Building ID
	 * @param {number} data.body.utilityCosts[].categoryId Utility cost category ID
	 * @param {object} data.body.utilityCosts[].cost 
	 * @param {string} data.body.utilityCosts[].cost.currency Utility cost currency
	 * @param {number} data.body.utilityCosts[].cost.share Utility cost share
	 * @param {number} data.body.utilityCosts[].cost.total Utility cost total
	 * @param {string} data.body.utilityCosts[].description Utility cost description
	 * @param {number} data.body.utilityCosts[].id Utility Cost ID
	 * @param {object} data.body.utilityCosts[].period 
	 * @param {number} data.body.utilityCosts[].period.days Utility cost days
	 * @param {string} data.body.utilityCosts[].period.from Utility cost period start date
	 * @param {string} data.body.utilityCosts[].period.to Utility cost period end date
	 * @param {number} data.body.utilityCosts[].rentalObjectId Rental object ID
	 * @param {string} data.body.utilityCosts[].type Utility cost type Enum: ['building', 'rental_object']
	 * @param {boolean} data.body.utilityCosts[].useInCalculation Use utility cost in calculation?

	 * @see {@link file:///./redoc-husleieapi.html#operation/generateOneLandlordUtilityCostValidation}
	 * @returns {Promise} A promise
	 */
	generateOneLandlordUtilityCostValidation(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "generateOneLandlordUtilityCostValidation" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"billingPeriodEnd":{"description":"Utility cost billing period end date","format":"date","type":"string"},"billingPeriodStart":{"description":"Utility cost billing period start date","format":"date","type":"string"},"landlordBankAccountNumber":{"description":"Landlord bank account number","type":"string"},"settlementDueDate":{"description":"Utility cost settlement due date","format":"date","type":"string"},"statementDate":{"description":"Utility cost settlement date","format":"date","type":"string"},"utilityCosts":{"items":{"properties":{"allocation":{"properties":{"part":{"description":"Utility cost allocation part","nullable":true,"type":"number"},"partShare":{"description":"Utility cost allocation part share","format":"float","nullable":true,"type":"number"},"partTotal":{"description":"Utility cost allocation part total","format":"float","nullable":true,"type":"number"}},"type":"object"},"allocationKeyId":{"description":"Allocation key ID","nullable":true,"type":"number"},"apportionable":{"description":"Utility cost apportionable","type":"boolean"},"buildingId":{"description":"Building ID","nullable":true,"type":"number"},"categoryId":{"description":"Utility cost category ID","type":"number"},"cost":{"properties":{"currency":{"description":"Utility cost currency","type":"string"},"share":{"description":"Utility cost share","format":"float","type":"number"},"total":{"description":"Utility cost total","format":"float","type":"number"}},"required":["share","total","currency"],"type":"object"},"description":{"description":"Utility cost description","type":"string"},"id":{"description":"Utility Cost ID","type":"number"},"period":{"properties":{"days":{"description":"Utility cost days","type":"number"},"from":{"description":"Utility cost period start date","format":"date","type":"string"},"to":{"description":"Utility cost period end date","format":"date","type":"string"}},"required":["days","from","to"],"type":"object"},"rentalObjectId":{"description":"Rental object ID","nullable":true,"type":"number"},"type":{"description":"Utility cost type","enum":["building","rental_object"],"type":"string"},"useInCalculation":{"description":"Use utility cost in calculation?","type":"boolean"}},"type":"object"},"type":"array"}},"required":["billingPeriodStart","billingPeriodEnd"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/tenancies/{tenancy_id}/validate', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getManyLandlordUtilityCostCalculations}
	 * @returns {Promise} A promise
	 */
	getManyLandlordUtilityCostCalculations(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getManyLandlordUtilityCostCalculations" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/calculations', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} utility_cost_calculation_id Required Utility Cost Calculation ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getOneLandlordUtilityCostCalculation}
	 * @returns {Promise} A promise
	 */
	getOneLandlordUtilityCostCalculation(landlord_id, utility_cost_calculation_id, responseType = 'json') {

		this.logger('Calling API function "getOneLandlordUtilityCostCalculation" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/calculations/{utility_cost_calculation_id}', {
			landlord_id,
			utility_cost_calculation_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} utility_cost_calculation_id Required Utility Cost Calculation ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.billingPeriodEnd Utility cost billing period end date
	 * @param {string} data.body.billingPeriodStart Utility cost billing period start date
	 * @param {string} data.body.landlordBankAccountNumber Landlord bank account number
	 * @param {string} data.body.settlementDueDate Utility cost settlement due date
	 * @param {string} data.body.statementDate Utility cost settlement date
	 * @param {array} data.body.utilityCosts 
	 * @param {object} data.body.utilityCosts[].allocation 
	 * @param {number} data.body.utilityCosts[].allocation.part Utility cost allocation part
	 * @param {number} data.body.utilityCosts[].allocation.partShare Utility cost allocation part share
	 * @param {number} data.body.utilityCosts[].allocation.partTotal Utility cost allocation part total
	 * @param {number} data.body.utilityCosts[].allocationKeyId Allocation key ID
	 * @param {boolean} data.body.utilityCosts[].apportionable Utility cost apportionable
	 * @param {number} data.body.utilityCosts[].buildingId Building ID
	 * @param {number} data.body.utilityCosts[].categoryId Utility cost category ID
	 * @param {object} data.body.utilityCosts[].cost 
	 * @param {string} data.body.utilityCosts[].cost.currency Utility cost currency
	 * @param {number} data.body.utilityCosts[].cost.share Utility cost share
	 * @param {number} data.body.utilityCosts[].cost.total Utility cost total
	 * @param {string} data.body.utilityCosts[].description Utility cost description
	 * @param {number} data.body.utilityCosts[].id Utility Cost ID
	 * @param {object} data.body.utilityCosts[].period 
	 * @param {number} data.body.utilityCosts[].period.days Utility cost days
	 * @param {string} data.body.utilityCosts[].period.from Utility cost period start date
	 * @param {string} data.body.utilityCosts[].period.to Utility cost period end date
	 * @param {number} data.body.utilityCosts[].rentalObjectId Rental object ID
	 * @param {string} data.body.utilityCosts[].type Utility cost type Enum: ['building', 'rental_object']
	 * @param {boolean} data.body.utilityCosts[].useInCalculation Use utility cost in calculation?

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateOneLandlordUtilityCostCalculation}
	 * @returns {Promise} A promise
	 */
	updateOneLandlordUtilityCostCalculation(landlord_id, utility_cost_calculation_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateOneLandlordUtilityCostCalculation" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"billingPeriodEnd":{"description":"Utility cost billing period end date","format":"date","type":"string"},"billingPeriodStart":{"description":"Utility cost billing period start date","format":"date","type":"string"},"landlordBankAccountNumber":{"description":"Landlord bank account number","type":"string"},"settlementDueDate":{"description":"Utility cost settlement due date","format":"date","type":"string"},"statementDate":{"description":"Utility cost settlement date","format":"date","type":"string"},"utilityCosts":{"items":{"properties":{"allocation":{"properties":{"part":{"description":"Utility cost allocation part","nullable":true,"type":"number"},"partShare":{"description":"Utility cost allocation part share","format":"float","nullable":true,"type":"number"},"partTotal":{"description":"Utility cost allocation part total","format":"float","nullable":true,"type":"number"}},"type":"object"},"allocationKeyId":{"description":"Allocation key ID","nullable":true,"type":"number"},"apportionable":{"description":"Utility cost apportionable","type":"boolean"},"buildingId":{"description":"Building ID","nullable":true,"type":"number"},"categoryId":{"description":"Utility cost category ID","type":"number"},"cost":{"properties":{"currency":{"description":"Utility cost currency","type":"string"},"share":{"description":"Utility cost share","format":"float","type":"number"},"total":{"description":"Utility cost total","format":"float","type":"number"}},"required":["share","total","currency"],"type":"object"},"description":{"description":"Utility cost description","type":"string"},"id":{"description":"Utility Cost ID","type":"number"},"period":{"properties":{"days":{"description":"Utility cost days","type":"number"},"from":{"description":"Utility cost period start date","format":"date","type":"string"},"to":{"description":"Utility cost period end date","format":"date","type":"string"}},"required":["days","from","to"],"type":"object"},"rentalObjectId":{"description":"Rental object ID","nullable":true,"type":"number"},"type":{"description":"Utility cost type","enum":["building","rental_object"],"type":"string"},"useInCalculation":{"description":"Use utility cost in calculation?","type":"boolean"}},"type":"object"},"type":"array"}},"required":["billingPeriodStart","billingPeriodEnd"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/accounting/utility_cost/calculations/{utility_cost_calculation_id}', {
			landlord_id,
			utility_cost_calculation_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}


window.___api_logging = false;

try {
	if ('localStorage' in window) {
		window.___api_logging = (window.localStorage.getItem('api_logging')) === 'true' ? true : false;
	}
} catch(e) {
	// eslint-disable-next-line no-console
	console.error(e);
}

window.api_logging = (s) => {
	if (s === undefined)
		return window.___api_logging;

	window.___api_logging = s ? true : false;

	try {
		if ('localStorage' in window) {
			window.localStorage.setItem('api_logging', window.___api_logging);
		}
		// eslint-disable-next-line no-console
		console.log('SmartmieteAPI: Logging set to ', window.___api_logging);
	} catch(e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}
};
